import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import EmailVerificationPage from './EmailVerificationPage';
import AdminDashboard from './AdminDashboard';
import GeneratedImagesPage from './GeneratedImagesPage';
import GeneratedVideosPage from './GeneratedVideosPage';
import SharedChatView from './SharedChatView';
import ThemeToggle from './ThemeToggle';
import ThemeTransition from './ThemeTransition';
import ChatHistoryPage from './ChatHistoryPage';
import { BlogListPage, BlogPostPage } from './BlogPage';  // Public blog components
import AdminBlog from './AdminBlog';                       // Admin blog management
import { HelmetProvider } from 'react-helmet-async';

import {
  MessageSquare,
  Image,
  Download as DownloadIcon,
  Share2,
  Copy as CopyIcon,
  Video,
  Sparkles,
  Brain,
  Home,
  User,
  LogOut,
  UserPlus,
  LogIn,
  History,
  Settings,
  CreditCard,
  Moon,
  Sun,
  Send,
  Copy,
  Check,
  X,
  Menu,
  ChevronRight,
  Crown,
  FileText,
  Search,
  Trash2,
  Edit,
  Plus,
  Bot,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Zap,
  Infinity,
  Shield,
  Cpu,
  Rocket,
  Code,
  Globe,
  Mail, 
  Palette,
  Film,
  BarChart3,
  Lock,
  Unlock,
  Upload,
  Download,
  ExternalLink,
  Star,
  TrendingUp,
  Users,
  Gift,
  Clock,
  Calendar,
  Award,
  Heart,
  Smile,
  ThumbsUp,
  Lightbulb,
  Compass,
  Layers,
  PenTool,
  Music,
  Camera,
  Mic,
  Terminal,
  Filter,
  Eye,
  EyeOff,
  Bell,
  Inbox,
  Flag,
  HelpCircle,
  Info,
  AlertTriangle,
  Coffee,
  Book,
  Church,
  Cross,
  Bible,
  Dove,
  Fish,
  Anchor,
  Feather,
  Leaf,
  Sun as SunIcon,
  Cloud,
  Star as StarIcon,
  Moon as MoonIcon,
  BookOpen
} from 'lucide-react';

// API base URL
export const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5050/api'
  : '/api';


// Add these new icon components
const CrossIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2v20M2 12h20" />
  </svg>
);

const BibleIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16v16H4zM8 8h8M8 12h8M8 16h4" />
  </svg>
);

const ChurchIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3L3 9v12h18V9l-9-6zM8 21v-6h8v6" />
  </svg>
);

const DoveIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2C7 2 3 6 3 11c0 5 4 9 9 9s9-4 9-9c0-5-4-9-9-9zM7 11l3 3 7-7" />
  </svg>
);

const FishIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 12c4-5 6-5 10-5s6 0 10 5c-4 5-6 5-10 5s-6 0-10-5zM12 7v2M12 15v2" />
  </svg>
);

// Light, bright biblical theme
const themes = {
  light: {
    background: 'bg-amber-50',
    text: 'text-stone-800',
    secondaryBg: 'bg-white',
    secondaryText: 'text-stone-600',
    border: 'border-amber-200',
    navbar: 'bg-white/95 backdrop-blur-sm border-amber-200',
    input: 'bg-white border-amber-300',
    button: 'bg-amber-600 hover:bg-amber-700',
    card: 'bg-white/90 border-amber-200 shadow-md',
    hover: 'hover:bg-amber-50',
    accent: 'text-amber-600',
    accentBg: 'bg-amber-100',
    accentBorder: 'border-amber-300',
    gradient: 'from-amber-400 to-orange-400',
    shadow: 'shadow-lg shadow-amber-100/50',
    highlight: 'bg-gradient-to-r from-amber-100 to-orange-100',
    scrollbar: 'scrollbar-amber',
    prose: 'prose-stone',
    messageBg: {
      user: 'bg-amber-100',
      assistant: 'bg-white'
    }
  },
  dark: {
    background: 'bg-stone-900',
    text: 'text-stone-100',
    secondaryBg: 'bg-stone-800',
    secondaryText: 'text-stone-400',
    border: 'border-stone-700',
    navbar: 'bg-stone-900/95 backdrop-blur-sm border-stone-700',
    input: 'bg-stone-800 border-stone-700',
    button: 'bg-amber-600 hover:bg-amber-700',
    card: 'bg-stone-800/90 border-stone-700',
    hover: 'hover:bg-stone-700',
    accent: 'text-amber-400',
    accentBg: 'bg-stone-700',
    accentBorder: 'border-stone-600',
    gradient: 'from-amber-500 to-orange-500',
    shadow: 'shadow-lg shadow-amber-900/20',
    highlight: 'bg-gradient-to-r from-amber-900/30 to-orange-900/30',
    scrollbar: 'scrollbar-stone',
    prose: 'prose-invert',
    messageBg: {
      user: 'bg-stone-700',
      assistant: 'bg-stone-800'
    }
  }
};

// Theme Context with proper types
export const ThemeContext = React.createContext({
  theme: 'light',
  toggleTheme: () => {},
  themeStyles: themes.light
});

// Generate anonymous ID for tracking
const generateAnonymousId = () => {
  const savedId = localStorage.getItem('anonymousId');
  if (savedId) return savedId;

  const newId = 'anon_' + Math.random().toString(36).substr(2, 9);
  localStorage.setItem('anonymousId', newId);
  return newId;
};

// Bible-themed Navbar Component
function Navbar({ isAuthenticated, user, logout }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme, themeStyles } = React.useContext(ThemeContext);

  if (location.pathname === '/success') {
    return null;
  }

  const isPaidUser = isAuthenticated && user?.plan === 'paid';

  return (
    <nav className={`sticky top-0 z-50 ${themeStyles.navbar} border-b ${themeStyles.border}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Bible-themed */}
          {/* Logo - Bible-themed */}
<Link to={isPaidUser ? "/chat" : "/"} className="flex items-center space-x-3 group">
  <div className={`w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-md`}>
    <img 
      src="https://app.gospelflixstream.com/bibleai.png" 
      alt="Bible AI Logo"
      className="w-full h-full object-cover"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = 'https://via.placeholder.com/40/amber-600/white?text=Bible';
      }}
    />
  </div>
  <div>
    <h1 className={`text-xl font-bold bg-gradient-to-r ${themeStyles.gradient} bg-clip-text text-transparent`}>
      Bible AI
    </h1>
    <div className="flex items-center space-x-1">
      <span className={`text-xs ${themeStyles.accent} font-medium`}>Wisdom • Faith • Light</span>
      <DoveIcon className={`w-3 h-3 ${themeStyles.accent}`} />
    </div>
  </div>
</Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {isPaidUser ? (
              <>
                <NavLink to="/chat" themeStyles={themeStyles}>Chat</NavLink>
                <NavLink to="/generate" themeStyles={themeStyles}>Create</NavLink>
                <div className="relative group">
                  <button className={`flex items-center space-x-1 px-3 py-2 text-sm font-medium ${themeStyles.secondaryText} hover:${themeStyles.text} transition-colors`}>
                    <span>My Gallery</span>
                    <ChevronRight className="w-4 h-4 transform group-hover:rotate-90 transition-transform" />
                  </button>
                  <div className={`absolute left-0 mt-2 w-48 ${themeStyles.secondaryBg} border ${themeStyles.border} rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50`}>
                    <Link to="/images" className={`flex items-center space-x-2 p-3 ${themeStyles.hover} rounded-t-xl ${themeStyles.text}`}>
                      <Image className={`w-4 h-4 ${themeStyles.accent}`} />
                      <span>My Images</span>
                    </Link>
                    <Link to="/videos" className={`flex items-center space-x-2 p-3 ${themeStyles.hover} rounded-b-xl ${themeStyles.text}`}>
                      <Film className={`w-4 h-4 ${themeStyles.accent}`} />
                      <span>My Videos</span>
                    </Link>
                  </div>
                </div>
                <NavLink to="/blog" themeStyles={themeStyles}>Blog</NavLink>
                <NavLink to="/profile" themeStyles={themeStyles}>Profile</NavLink>
              </>
            ) : (
              <>
                <NavLink to="/" themeStyles={themeStyles}>Home</NavLink>
                <NavLink to="/chat" themeStyles={themeStyles}>Chat</NavLink>
                <NavLink to="/generate" themeStyles={themeStyles}>Create</NavLink>
                <NavLink to="/pricing" themeStyles={themeStyles}>Pricing</NavLink>
                <NavLink to="/blog" themeStyles={themeStyles}>Blog</NavLink>
              </>
            )}
            <NavLink to="/contact" themeStyles={themeStyles}>Contact</NavLink>
            <NavLink to="/privacy" themeStyles={themeStyles}>Privacy</NavLink>

            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <div className="relative group">
                  <button className={`flex items-center space-x-2 px-3 py-2 ${themeStyles.secondaryBg} ${themeStyles.hover} rounded-xl transition-colors ${themeStyles.text} shadow-sm`}>
                    <User className="w-4 h-4" />
                    <span className="text-sm">{user?.email?.split('@')[0]}</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <div className={`absolute right-0 mt-2 w-48 ${themeStyles.secondaryBg} border ${themeStyles.border} rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50`}>
                    <Link to="/profile" className={`flex items-center space-x-2 p-3 ${themeStyles.hover} rounded-t-xl ${themeStyles.text}`}>
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </Link>
                    <Link to="/history" className={`flex items-center space-x-2 p-3 ${themeStyles.hover} ${themeStyles.text}`}>
                      <History className="w-4 h-4" />
                      <span>History</span>
                    </Link>
                    <button
                      onClick={logout}
                      className="flex items-center space-x-2 p-3 hover:bg-red-100 text-red-600 w-full rounded-b-xl"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
                <ThemeToggle 
                  theme={theme} 
                  toggleTheme={toggleTheme} 
                  themeStyles={themeStyles} 
                />
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login" className={`px-4 py-2 ${themeStyles.button} rounded-xl text-sm font-medium transition-colors text-white shadow-md hover:shadow-lg`}>
                  Sign In
                </Link>
                <Link to="/signup" className={`px-4 py-2 ${themeStyles.button} rounded-xl text-sm font-medium transition-colors text-white shadow-md hover:shadow-lg`}>
                  Sign Up
                </Link>
                <ThemeToggle 
                  theme={theme} 
                  toggleTheme={toggleTheme} 
                  themeStyles={themeStyles} 
                />
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden p-2 ${themeStyles.secondaryBg} rounded-xl ${themeStyles.text} shadow-sm`}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`md:hidden ${themeStyles.secondaryBg} backdrop-blur-lg rounded-xl border ${themeStyles.border} overflow-hidden shadow-xl`}
            >
              <div className="p-4 space-y-2">
                {isPaidUser ? (
                  <>
                    <MobileNavLink to="/chat" icon={<MessageSquare />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Chat
                    </MobileNavLink>
                    <MobileNavLink to="/generate" icon={<Palette />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Create
                    </MobileNavLink>
                    
                    <div className="relative">
                      <div className={`flex items-center justify-between px-3 py-2 text-xs font-medium ${themeStyles.secondaryText} uppercase tracking-wider`}>
                        <span>My Gallery</span>
                      </div>
                      <div className="ml-4 space-y-1">
                        <MobileNavLink to="/images" icon={<Image className={`w-4 h-4 ${themeStyles.accent}`} />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                          My Images
                        </MobileNavLink>
                        <MobileNavLink to="/videos" icon={<Film className={`w-4 h-4 ${themeStyles.accent}`} />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                          My Videos
                        </MobileNavLink>
                        <MobileNavLink to="/blog" icon={<BookOpen />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
  Blog
</MobileNavLink>
                      </div>
                    </div>
                    
                    <MobileNavLink to="/profile" icon={<User />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Profile
                    </MobileNavLink>
                  </>
                ) : (
                  <>
                    <MobileNavLink to="/" icon={<Home />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Home
                    </MobileNavLink>
                    <MobileNavLink to="/chat" icon={<MessageSquare />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Chat
                    </MobileNavLink>
                    <MobileNavLink to="/generate" icon={<Palette />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Create
                    </MobileNavLink>
                    <MobileNavLink to="/pricing" icon={<CreditCard />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Pricing
                    </MobileNavLink>
                    <MobileNavLink to="/blog" icon={<BookOpen />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
  Blog
</MobileNavLink>
                  </>
                )}
                
                <MobileNavLink to="/contact" icon={<Mail />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                  Contact
                </MobileNavLink>
                <MobileNavLink to="/privacy" icon={<Shield />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                  Privacy
                </MobileNavLink>

                <ThemeToggle 
                  theme={theme} 
                  toggleTheme={toggleTheme} 
                  themeStyles={themeStyles} 
                />

                {isAuthenticated ? (
                  <>
                    {!isPaidUser && (
                      <div className="relative">
                        <div className={`flex items-center justify-between px-3 py-2 text-xs font-medium ${themeStyles.secondaryText} uppercase tracking-wider`}>
                          <span>My Gallery</span>
                        </div>
                        <div className="ml-4 space-y-1">
                          <MobileNavLink to="/images" icon={<Image className={`w-4 h-4 ${themeStyles.accent}`} />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                            My Images
                          </MobileNavLink>
                          <MobileNavLink to="/videos" icon={<Film className={`w-4 h-4 ${themeStyles.accent}`} />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                            My Videos
                          </MobileNavLink>
                        </div>
                      </div>
                    )}
                    
                    <MobileNavLink to="/profile" icon={<User />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Profile
                    </MobileNavLink>
                    <MobileNavLink to="/history" icon={<History />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Chat History
                    </MobileNavLink>
                    <button
                      onClick={() => {
                        logout();
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center space-x-3 w-full p-3 text-red-600 hover:bg-red-50 rounded-xl"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <>
                    <Link 
                      to="/login" 
                      className={`flex items-center justify-center space-x-2 w-full p-3 ${themeStyles.secondaryBg} border ${themeStyles.border} rounded-xl text-sm font-medium ${themeStyles.text} shadow-sm`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <LogIn className="w-4 h-4" />
                      <span>Sign In</span>
                    </Link>
                    <Link 
                      to="/signup" 
                      className={`flex items-center justify-center space-x-2 w-full p-3 ${themeStyles.button} text-white rounded-xl text-sm font-medium shadow-md`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Sign Up</span>
                    </Link>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}

// Updated NavLink component with theme support
function NavLink({ to, children, themeStyles }) {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={`px-3 py-2 text-sm font-medium transition-colors ${
        isActive
          ? `${themeStyles.accent} font-semibold`
          : themeStyles.secondaryText
      } hover:${themeStyles.text}`}
    >
      {children}
    </Link>
  );
}

function MobileNavLink({ to, icon, children, onClick, themeStyles }) {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center space-x-3 p-3 rounded-xl transition-colors ${
        isActive
          ? `${themeStyles.accentBg} ${themeStyles.text}`
          : `${themeStyles.secondaryText} hover:${themeStyles.secondaryBg} hover:${themeStyles.text}`
      }`}
    >
      <div className={isActive ? themeStyles.accent : ''}>{icon}</div>
      <span className="text-sm">{children}</span>
    </Link>
  );
}

// Sidebar Component with Bible theme
function Sidebar({ isOpen, onClose, chats, activeChat, onNewChat, onSelectChat, onDeleteChat }) {
  const [searchQuery, setSearchQuery] = useState('');
  const { themeStyles } = React.useContext(ThemeContext);

  const filteredChats = chats.filter(chat =>
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 md:hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ x: -300 }}
        animate={{ x: 0 }}
        exit={{ x: -300 }}
        className={`absolute left-0 top-0 h-full w-80 ${themeStyles.secondaryBg} border-r ${themeStyles.border} shadow-xl`}
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={onNewChat}
              className={`flex items-center space-x-2 px-3 py-2 ${themeStyles.button} rounded-xl text-sm font-medium transition-colors text-white shadow-md`}
            >
              <Plus className="w-4 h-4" />
              <span>New chat</span>
            </button>
            <button
              onClick={onClose}
              className={`p-2 ${themeStyles.hover} rounded-xl transition-colors`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="relative mb-4">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${themeStyles.secondaryText}`} />
            <input
              type="text"
              placeholder="Search chats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 ${themeStyles.input} rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 ${themeStyles.text}`}
            />
          </div>

          <div className="space-y-1">
            {filteredChats.map((chat) => (
              <div
                key={chat.id}
                className={`group flex items-center justify-between p-3 rounded-xl cursor-pointer transition-colors ${
                  activeChat === chat.id
                    ? `${themeStyles.accentBg} border-l-4 border-amber-500`
                    : themeStyles.hover
                }`}
                onClick={() => onSelectChat(chat.id)}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {chat.title}
                  </p>
                  <p className={`text-xs ${themeStyles.secondaryText} mt-1`}>
                    {new Date(chat.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteChat(chat.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 text-red-500 rounded transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function ChatPage({ isAuthenticated, user, anonymousUsage, setAnonymousUsage }) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [chatTitle, setChatTitle] = useState('New Chat');
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [showLimitBanner, setShowLimitBanner] = useState(false);
  const [showLimitBannerb, setShowLimitBannerb] = useState(false);
  const messagesEndRef = useRef(null);
  const { themeStyles } = React.useContext(ThemeContext);

  useEffect(() => {
    if (isAuthenticated) {
      fetchChats();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingResponse]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchChats = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API_URL}/chat/history`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setChats(response.data.chats || []);
    } catch (error) {
      console.error('Failed to fetch chats:', error.response?.data || error.message);
    }
  };
  
  const generateChatTitle = async (chatId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/chat/generate-title/${chatId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setChatTitle(response.data.title);
        fetchChats();
        return response.data.title;
      }
    } catch (error) {
      console.error('Failed to generate title:', error);
    }
    return null;
  };

  const createNewChat = () => {
    setMessages([]);
    setActiveChat(null);
    setChatTitle('New Chat');
    setSidebarOpen(false);
    setStreamingResponse('');
  };

  const selectChat = async (chatId) => {
    setIsLoadingChat(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please login to load chats');
        return;
      }
      
      const response = await axios.get(`${API_URL}/chat/history/${chatId}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.chat) {
        const chat = response.data.chat;
        setMessages(chat.messages || []);
        setActiveChat(chat.id);
        setChatTitle(chat.title || 'New Chat');
        setSidebarOpen(false);
        setStreamingResponse('');
        setIsStreaming(false);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Failed to load chat:', error.response?.data || error.message);
      alert('Failed to load chat. Please try again.');
    } finally {
      setIsLoadingChat(false);
    }
  };

  const deleteChat = async (chatId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this chat?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_URL}/chat/history/${chatId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (activeChat === chatId) {
          createNewChat();
        }
        fetchChats();
      } catch (error) {
        console.error('Failed to delete chat:', error);
      }
    }
  };

  const updateChatTitle = async (chatId, newTitle) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/chat/history/${chatId}`, 
        { title: newTitle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchChats();
    } catch (error) {
      console.error('Failed to update chat title:', error);
    }
  };
  
  const downloadChat = async () => {
    if (!activeChat) {
      alert('No chat selected to download');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please login to download chats');
        return;
      }

      const baseUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:4040'
        : '';
      
      const downloadUrl = `${baseUrl}${API_URL}/chat/download/${activeChat}?format=txt`;
      
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', errorText);
        
        if (response.status === 401) {
          alert('Authentication failed. Please login again.');
          return;
        }
        throw new Error(`Download failed: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        console.error('Received HTML instead of file download');
        alert('Download failed: Server returned HTML. This might be a routing issue.');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-${chatTitle?.replace(/[^a-z0-9]/gi, '_') || 'conversation'}-${new Date().getTime()}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
    } catch (error) {
      console.error('Failed to download chat:', error);
      alert('Failed to download chat. Please try again.');
    }
  };

  const shareChat = async () => {
    if (!activeChat) {
      alert('No chat selected to share');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/chat/share/${activeChat}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ expiresIn: 7 })
      });

      const data = await response.json();
      if (data.success) {
        navigator.clipboard.writeText(data.shareUrl);
        alert('Share link copied to clipboard! (expires in 7 days)');
      } else {
        alert('Failed to create share link');
      }
    } catch (error) {
      console.error('Failed to share chat:', error);
      alert('Failed to share chat. Please try again.');
    }
  };

  const saveChatHistory = async (chatMessages) => {
    if (!isAuthenticated || chatMessages.length === 0) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/chat/history`, {
        messages: chatMessages,
        chatId: activeChat
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.chat) {
        if (!activeChat) {
          setActiveChat(response.data.chat.id);
          setChatTitle(response.data.chat.title || 'New Chat');
        } else {
          if (response.data.chat.title !== chatTitle) {
            setChatTitle(response.data.chat.title);
          }
        }
        fetchChats();
        
        if (chatMessages.length >= 3) {
          setTimeout(() => {
            if (chatTitle === 'New Chat' || chatTitle.includes('...') || chatTitle.length < 10) {
              generateChatTitle(response.data.chat.id || activeChat);
            }
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Failed to save chat history:', error.response?.data || error.message);
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || isLoading || isStreaming) return;

    if (!isAuthenticated) {
      const currentUsage = JSON.parse(localStorage.getItem('anonymousUsage') || '{"chat":0,"image":0,"video":0}');
      if (currentUsage.chat >= 5) {
        setShowLimitBanner(true);
        return;
      }
    }

    if (isAuthenticated && user?.plan !== 'paid') {
      try {
        const response = await axios.get(`${API_URL}/usage/limits`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        const { limits } = response.data;
        if (limits.chat.used >= limits.chat.max) {
          setShowLimitBannerb(true);
          return;
        }
      } catch (error) {
        console.error('Failed to check usage limits:', error);
      }
    }

    setIsLoading(true);
    setIsStreaming(true);
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    const currentMessage = message;
    setMessage('');
    setStreamingResponse('');

    try {
      const headers = {};
      if (!isAuthenticated) {
        headers['x-anonymous-id'] = generateAnonymousId();
      }

      const response = await fetch(`${API_URL}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
          ...(isAuthenticated ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {})
        },
        body: JSON.stringify({
          message: currentMessage,
          history: messages,
          stream: true
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              setIsStreaming(false);
              setIsLoading(false);

              const aiMessage = {
                role: 'assistant',
                content: fullResponse,
                timestamp: new Date().toISOString()
              };
              const updatedMessages = [...newMessages, aiMessage];
              setMessages(updatedMessages);
              setStreamingResponse('');

              if (isAuthenticated) {
                saveChatHistory(updatedMessages);
                
                if (updatedMessages.length >= 3) {
                  const currentTitle = chatTitle || '';
                  if (currentTitle === 'New Chat' || 
                      currentTitle.includes('...') || 
                      currentTitle.length < 15) {
                    setTimeout(() => {
                      generateChatTitle(activeChat);
                    }, 1500);
                  }
                }
              }

              if (!isAuthenticated) {
                const currentUsage = JSON.parse(localStorage.getItem('anonymousUsage') || '{"chat":0,"image":0,"video":0}');
                currentUsage.chat = (currentUsage.chat || 0) + 1;
                localStorage.setItem('anonymousUsage', JSON.stringify(currentUsage));
                setAnonymousUsage(currentUsage);
              }

              return;
            }

            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                fullResponse += parsed.content;
                setStreamingResponse(fullResponse);
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      setIsStreaming(false);
      setIsLoading(false);
      alert('Chat failed. Please try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const copyMessage = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Loading Overlay */}
      {isLoadingChat && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`${themeStyles.secondaryBg} p-6 rounded-xl shadow-xl flex items-center space-x-3`}>
            <div className={`w-5 h-5 border-2 ${themeStyles.accent} border-t-transparent rounded-full animate-spin`}></div>
            <span>Loading chat...</span>
          </div>
        </div>
      )}
      
      {/* Limit Banner */}
      {showLimitBanner && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4 backdrop-blur-sm shadow-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-sm">Free chat limit reached!</p>
                  <p className={`${themeStyles.secondaryText} text-xs`}>Sign up to continue chatting</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Link
                  to="/signup"
                  onClick={() => setShowLimitBanner(false)}
                  className={`px-3 py-1 ${themeStyles.button} rounded-lg text-xs text-white shadow-md`}
                >
                  Sign Up
                </Link>
                <button
                  onClick={() => setShowLimitBanner(false)}
                  className={`p-1 ${themeStyles.hover} rounded`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Limit Bannerb */}
      {showLimitBannerb && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4 backdrop-blur-sm shadow-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-sm">Free chat limit reached!</p>
                  <p className={`${themeStyles.secondaryText} text-xs`}>Upgrade for only $15 lifetime plan</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Link
                  to="/pricing"
                  onClick={() => setShowLimitBanner(false)}
                  className={`px-3 py-1 ${themeStyles.button} rounded-lg text-xs text-white shadow-md`}
                >
                  Upgrade
                </Link>
                <button
                  onClick={() => setShowLimitBanner(false)}
                  className={`p-1 ${themeStyles.hover} rounded`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className={`hidden md:flex flex-col w-64 border-r ${themeStyles.border} ${themeStyles.secondaryBg}`}>
        <div className="p-4">
          <button
            onClick={createNewChat}
            className={`flex items-center space-x-2 w-full px-3 py-2 ${themeStyles.button} rounded-xl text-sm font-medium transition-colors text-white shadow-md hover:shadow-lg`}
          >
            <Plus className="w-4 h-4" />
            <span>New chat</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-2">
            <div className={`text-xs font-medium ${themeStyles.secondaryText} uppercase tracking-wider mb-2 flex items-center`}>
              <Book className={`w-3 h-3 mr-1 ${themeStyles.accent}`} />
              Recent Chats
            </div>
            <div className="space-y-1">
              {chats.map((chat) => (
                <div
                  key={chat.id}
                  className={`group flex items-center justify-between p-2 rounded-xl cursor-pointer transition-colors ${
                    activeChat === chat.id
                      ? `${themeStyles.accentBg} border-l-4 border-amber-500`
                      : themeStyles.hover
                  }`}
                  onClick={() => selectChat(chat.id)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {chat.title || 'Untitled Chat'}
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <p className={`text-xs ${themeStyles.secondaryText}`}>
                        {chat.message_count || 0} messages
                      </p>
                      <p className={`text-xs ${themeStyles.secondaryText}`}>
                        {new Date(chat.updated_at || chat.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newTitle = prompt('Edit chat title:', chat.title);
                        if (newTitle && newTitle !== chat.title) {
                          updateChatTitle(chat.id, newTitle);
                        }
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-amber-100 rounded transition-all"
                      title="Edit title"
                    >
                      <Edit className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => deleteChat(chat.id, e)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 text-red-500 rounded transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="px-4 py-2 mb-4">
          <div className={`text-xs font-medium ${themeStyles.secondaryText} uppercase tracking-wider mb-2 flex items-center`}>
            <Image className={`w-3 h-3 mr-1 ${themeStyles.accent}`} />
            My Gallery
          </div>
          <div className="space-y-1">
            <Link
              to="/images"
              className={`flex items-center space-x-3 px-3 py-2 text-sm ${themeStyles.secondaryText} hover:${themeStyles.text} ${themeStyles.hover} rounded-xl transition-colors`}
            >
              <Image className={`w-4 h-4 ${themeStyles.accent}`} />
              <span>My Images</span>
            </Link>
            <Link
              to="/videos"
              className={`flex items-center space-x-3 px-3 py-2 text-sm ${themeStyles.secondaryText} hover:${themeStyles.text} ${themeStyles.hover} rounded-xl transition-colors`}
            >
              <Film className={`w-4 h-4 ${themeStyles.accent}`} />
              <span>My Videos</span>
            </Link>
          </div>
        </div>

        <div className={`border-t ${themeStyles.border} my-4`}></div>
        
        <div className={`p-4 border-t ${themeStyles.border}`}>
          {isAuthenticated ? (
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${themeStyles.gradient} flex items-center justify-center shadow-md`}>
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.email}</p>
                <p className={`text-xs ${themeStyles.secondaryText}`}>
                  {user?.plan === 'paid' ? 'Pro Plan' : 'Free Plan'}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className={`text-sm ${themeStyles.secondaryText} mb-2`}>Free chats remaining: {5 - (anonymousUsage?.chat || 0)}</p>
              <Link
                to="/login"
                className={`text-sm ${themeStyles.accent} hover:underline font-medium`}
              >
                Sign in for more
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        chats={chats}
        activeChat={activeChat}
        onNewChat={createNewChat}
        onSelectChat={selectChat}
        onDeleteChat={deleteChat}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Chat Header */}
        <div className={`flex items-center justify-between p-4 border-b ${themeStyles.border} ${themeStyles.secondaryBg}`}>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className={`md:hidden p-2 ${themeStyles.hover} rounded-xl transition-colors`}
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-2">
              {activeChat && isAuthenticated ? (
                <input
                  type="text"
                  value={chatTitle}
                  onChange={(e) => setChatTitle(e.target.value)}
                  onBlur={() => updateChatTitle(activeChat, chatTitle)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      updateChatTitle(activeChat, chatTitle);
                    }
                  }}
                  className={`bg-transparent text-lg font-semibold border-b border-transparent hover:${themeStyles.border} focus:border-amber-500 focus:outline-none px-1 min-w-[200px] ${themeStyles.text}`}
                />
              ) : (
                <h2 className="text-lg font-semibold flex items-center">
                  <Book className={`w-5 h-5 mr-2 ${themeStyles.accent}`} />
                  {chatTitle}
                </h2>
              )}
              {activeChat && (
                <button
                  onClick={() => updateChatTitle(activeChat, chatTitle)}
                  className={`p-1 ${themeStyles.hover} rounded`}
                  title="Save title"
                >
                  <Check className={`w-4 h-4 ${themeStyles.accent}`} />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {activeChat && isAuthenticated && (
              <>
                <button
                  onClick={downloadChat}
                  className={`p-2 ${themeStyles.hover} rounded-xl transition-colors`}
                  title="Download chat"
                >
                  <DownloadIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={shareChat}
                  className={`p-2 ${themeStyles.hover} rounded-xl transition-colors`}
                  title="Share chat"
                >
                  <Share2 className="w-4 h-4" />
                </button>
              </>
            )}
            
            {!isAuthenticated && (
              <span className={`text-sm ${themeStyles.secondaryText}`}>
                Free: {5 - (anonymousUsage?.chat || 0)} left
              </span>
            )}
            {isAuthenticated && user?.plan === 'paid' && (
              <span className={`flex items-center space-x-1 px-2 py-1 bg-gradient-to-r ${themeStyles.gradient} text-white rounded-full text-xs shadow-md`}>
                <Crown className="w-3 h-3" />
                <span>Pro</span>
              </span>
            )}
          </div>
        </div>

        {/* Messages Container */}
        <div className={`flex-1 overflow-y-auto p-4 ${themeStyles.background}`}>
          {messages.length === 0 && !isStreaming ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <div className={`w-24 h-24 rounded-2xl overflow-hidden mb-6 shadow-xl transform hover:scale-105 transition-transform`}>
    <img 
      src="https://app.gospelflixstream.com/bibleai.png" 
      alt="Bible AI"
      className="w-full h-full object-cover"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = 'https://via.placeholder.com/96/amber-600/white?text=Bible';
      }}
    />
  </div>
              <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r ${themeStyles.gradient} bg-clip-text text-transparent">
                Bible AI
              </h3>
              <p className={`${themeStyles.secondaryText} max-w-md mb-8 text-lg`}>
                Ask questions about faith, scripture, and wisdom
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
                {[
                  "What does the Bible say about love?",
                  "Explain the meaning of Psalm 23",
                  "Tell me about the life of Jesus",
                  "What is the greatest commandment?"
                ].map((prompt, idx) => (
                  <button
                    key={idx}
                    onClick={() => setMessage(prompt)}
                    className={`px-4 py-3 ${themeStyles.card} ${themeStyles.hover} rounded-xl text-sm text-left transition-all hover:shadow-lg border ${themeStyles.border}`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex gap-4 ${msg.role === 'user' ? 'justify-end' : ''}`}
                >
                  {msg.role === 'assistant' && (
  <div className={`w-8 h-8 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 shadow-md`}>
    <img 
      src="https://app.gospelflixstream.com/bibleai.png" 
      alt="Bible AI Assistant"
      className="w-full h-full object-cover"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = 'https://via.placeholder.com/32/amber-600/white?text=AI';
      }}
    />
  </div>
)}
                  <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-first' : ''}`}>
                    <div className={`p-4 rounded-xl shadow-md ${
                      msg.role === 'user'
                        ? themeStyles.messageBg.user
                        : themeStyles.messageBg.assistant
                    } border ${themeStyles.border}`}>
                      <ReactMarkdown className={`prose ${themeStyles.prose} max-w-none text-sm`}>
                        {msg.content}
                      </ReactMarkdown>
                    </div>

                    <div className="flex items-center justify-between mt-2 px-1">
                      <span className={`text-xs ${themeStyles.secondaryText}`}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(msg.content);
                          const btn = document.getElementById(`copy-btn-${index}`);
                          if (btn) {
                            const originalIcon = btn.innerHTML;
                            btn.innerHTML = '<svg class="w-3 h-3 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20 6L9 17L4 12" strokeWidth="2"/></svg>';
                            setTimeout(() => {
                              btn.innerHTML = originalIcon;
                            }, 2000);
                          }
                        }}
                        id={`copy-btn-${index}`}
                        className={`text-xs ${themeStyles.secondaryText} hover:${themeStyles.text} transition-colors p-1`}
                        title="Copy message"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {msg.role === 'user' && (
                    <div className={`w-8 h-8 rounded-xl ${themeStyles.secondaryBg} border ${themeStyles.border} flex items-center justify-center flex-shrink-0 shadow-md`}>
                      <User className={`w-4 h-4 ${themeStyles.accent}`} />
                    </div>
                  )}
                </div>
              ))}

              {/* Streaming Response */}
              {isStreaming && streamingResponse && (
                <div className="flex gap-4">
                  <div className={`w-8 h-8 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 shadow-md`}>
      <img 
        src="https://app.gospelflixstream.com/bibleai.png" 
        alt="Bible AI Assistant"
        className="w-full h-full object-cover"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = 'https://via.placeholder.com/32/amber-600/white?text=AI';
        }}
      />
    </div>

                  <div className={`max-w-[80%] p-4 rounded-xl shadow-md ${themeStyles.messageBg.assistant} border ${themeStyles.border}`}>
                    <ReactMarkdown className={`prose ${themeStyles.prose} max-w-none text-sm`}>
                      {streamingResponse}
                    </ReactMarkdown>
                    <div className="flex space-x-1 mt-2">
                      <div className={`w-1 h-1 ${themeStyles.accent} rounded-full animate-pulse`}></div>
                      <div className={`w-1 h-1 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.1s' }}></div>
                      <div className={`w-1 h-1 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}

              {(isLoading || isStreaming) && !streamingResponse && (
                <div className="flex gap-4">
                      <div className={`w-8 h-8 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 shadow-md`}>
      <img 
        src="https://app.gospelflixstream.com/bibleai.png" 
        alt="Bible AI Assistant"
        className="w-full h-full object-cover"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = 'https://via.placeholder.com/32/amber-600/white?text=AI';
        }}
      />
    </div>
                  <div className={`p-4 rounded-xl shadow-md ${themeStyles.messageBg.assistant} border ${themeStyles.border}`}>
                    <div className="flex space-x-2">
                      <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-bounce`}></div>
                      <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-bounce`} style={{ animationDelay: '0.1s' }}></div>
                      <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-bounce`} style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area - IMPROVED VISIBILITY */}
        <div className={`border-t ${themeStyles.border} p-4 ${themeStyles.secondaryBg} shadow-inner`}>
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <textarea
  value={message}
  onChange={(e) => setMessage(e.target.value)}
  onKeyPress={handleKeyPress}
  placeholder="Ask a question about faith, scripture, or wisdom..."
  className={`w-full p-4 bg-white border-2 border-amber-300 rounded-xl resize-none focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 text-sm min-h-[100px] max-h-[200px] text-stone-800 shadow-lg placeholder-stone-500`}
  rows="3"
  disabled={isLoading || isStreaming}
  style={{ 
    backgroundColor: '#ffffff',
    borderColor: '#fcd34d',
    color: '#292524'
  }}
/>
              <div className="absolute bottom-3 right-3 flex items-center space-x-2">
                <button
                  onClick={() => setMessage('')}
                  className={`p-2 bg-white hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50 border border-amber-200 shadow-sm`}
                  style={{ color: '#b45309' }}
                  disabled={!message || isLoading || isStreaming}
                >
                  <X className="w-4 h-4" />
                </button>
                <button
                  onClick={sendMessage}
                  disabled={isLoading || isStreaming || !message.trim()}
                  className={`p-2 rounded-lg transition-colors shadow-md ${
                    isLoading || isStreaming || !message.trim()
                      ? 'bg-amber-100 text-amber-400 cursor-not-allowed border border-amber-200'
                      : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg hover:shadow-xl'
                  }`}
                >
                  {(isLoading || isStreaming) ? (
                    <div className="w-5 h-5 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="mt-3 text-xs text-center">
              <p className="text-amber-700/70 font-medium">
                Bible AI provides insights based on scripture. Always verify with your own study.
                {!isAuthenticated && (
                  <span className="ml-2 text-amber-600 font-semibold">
                    Free chats: {5 - (anonymousUsage?.chat || 0)} remaining
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Enhanced Generate Page (Bible Art Studio)
function GeneratePage({ isAuthenticated, user, anonymousUsage, setAnonymousUsage }) {
  const [activeTab, setActiveTab] = useState('image');
  const [imagePrompt, setImagePrompt] = useState('');
  const [videoPrompt, setVideoPrompt] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [imageStyle, setImageStyle] = useState('biblical');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [showSignupBanner, setShowSignupBanner] = useState(false);
  const [freeUsage, setFreeUsage] = useState(() => {
    return JSON.parse(localStorage.getItem('freeUsage') || '{"image":0,"video":0}');
  });
  const fileInputRef = useRef(null);
  const { themeStyles } = React.useContext(ThemeContext);

  const styles = [
    { id: 'biblical', name: 'Biblical Art', icon: '📜' },
    { id: 'renaissance', name: 'Renaissance', icon: '🎨' },
    { id: 'illuminated', name: 'Illuminated', icon: '✨' },
    { id: 'modern', name: 'Modern', icon: '🖼️' },
    { id: 'iconography', name: 'Iconography', icon: '🕊️' },
    { id: 'stained-glass', name: 'Stained Glass', icon: '⛪' }
  ];

  const aspectRatios = [
    { id: '1:1', name: 'Square', icon: '⬜' },
    { id: '16:9', name: 'Widescreen', icon: '📺' },
    { id: '9:16', name: 'Portrait', icon: '📱' },
    { id: '4:3', name: 'Standard', icon: '🖥️' }
  ];

  const examplePrompts = [
    "Moses parting the Red Sea, dramatic lighting, biblical art style",
    "The Garden of Eden with Adam and Eve, peaceful atmosphere",
    "Jesus teaching the Sermon on the Mount, Renaissance style",
    "David and Goliath, epic battle scene, dramatic lighting",
    "Noah's Ark with animals, rainbow in the sky",
    "The Last Supper, illuminated manuscript style"
  ];

  useEffect(() => {
    if (!isAuthenticated) {
      setShowSignupBanner(true);
    }
  }, [isAuthenticated]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateImage = async () => {
    if (!imagePrompt.trim()) {
      alert('Please enter a prompt');
      return;
    }
    
    if (!isAuthenticated) {
      setShowSignupBanner(true);
      return;
    }
    
    if (isAuthenticated && user?.plan !== 'paid') {
      if (freeUsage.image >= 1) {
        setShowLimitWarning(true);
        setTimeout(() => setShowLimitWarning(false), 3000);
        return;
      }
    }
    
    setIsGenerating(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/ai/text-to-image`, {
        prompt: `${imageStyle} style, ${imagePrompt}`,
        width: getWidthFromAspectRatio(aspectRatio),
        height: getHeightFromAspectRatio(aspectRatio),
        style: imageStyle
      }, { 
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      
      if (response.data.image_url) {
        setGeneratedImage(response.data.image_url);
        
        if (isAuthenticated && user?.plan !== 'paid') {
          const updatedUsage = { ...freeUsage, image: freeUsage.image + 1 };
          setFreeUsage(updatedUsage);
          localStorage.setItem('freeUsage', JSON.stringify(updatedUsage));
        }
      } else {
        throw new Error('No image URL returned');
      }
    } catch (error) {
      console.error('Image generation error:', error);
      if (error.response?.status === 402) {
        setShowLimitWarning(true);
        setTimeout(() => setShowLimitWarning(false), 3000);
      } else {
        alert(error.response?.data?.error || 'Image generation failed. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const generateVideo = async () => {
    if (!videoPrompt.trim() && !selectedImage) {
      alert('Please provide both a prompt and an image');
      return;
    }
    
    if (!isAuthenticated) {
      setShowSignupBanner(true);
      return;
    }
    
    if (isAuthenticated && user?.plan !== 'paid') {
      if (freeUsage.video >= 1) {
        setShowLimitWarning(true);
        setTimeout(() => setShowLimitWarning(false), 3000);
        return;
      }
    }
    
    setIsGenerating(true);
    const formData = new FormData();
    formData.append('prompt', videoPrompt);
    if (selectedImage) {
      formData.append('image', selectedImage);
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/ai/image-to-video`, formData, { 
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      
      if (response.data.video_url) {
        setGeneratedVideo(response.data.video_url);
        
        if (isAuthenticated && user?.plan !== 'paid') {
          const updatedUsage = { ...freeUsage, video: freeUsage.video + 1 };
          setFreeUsage(updatedUsage);
          localStorage.setItem('freeUsage', JSON.stringify(updatedUsage));
        }
      } else {
        throw new Error('No video URL returned');
      }
    } catch (error) {
      console.error('Video generation error:', error);
      if (error.response?.status === 402) {
        setShowLimitWarning(true);
        setTimeout(() => setShowLimitWarning(false), 3000);
      } else {
        alert(error.response?.data?.error || 'Video generation failed. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const getWidthFromAspectRatio = (ratio) => {
    switch (ratio) {
      case '1:1': return 512;
      case '16:9': return 768;
      case '9:16': return 512;
      case '4:3': return 682;
      default: return 512;
    }
  };

  const getHeightFromAspectRatio = (ratio) => {
    switch (ratio) {
      case '1:1': return 512;
      case '16:9': return 432;
      case '9:16': return 910;
      case '4:3': return 512;
      default: return 512;
    }
  };

  const downloadImage = () => {
    if (generatedImage) {
      const link = document.createElement('a');
      link.href = generatedImage;
      link.download = `bible-ai-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const downloadVideo = () => {
    if (generatedVideo) {
      const link = document.createElement('a');
      link.href = generatedVideo;
      link.download = `bible-ai-video-${Date.now()}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const copyPrompt = (prompt) => {
    if (activeTab === 'image') {
      setImagePrompt(prompt);
    } else {
      setVideoPrompt(prompt);
    }
  };

  const clearImage = () => {
    setSelectedImage(null);
    setPreviewImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const clearOutput = () => {
    if (activeTab === 'image') {
      setGeneratedImage(null);
    } else {
      setGeneratedVideo(null);
    }
  };

  return (
    <div className={`min-h-[calc(100vh-4rem)] p-4 ${themeStyles.background}`}>
      {/* Signup Banner for non-logged-in users */}
      {!isAuthenticated && showSignupBanner && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
          <div className={`bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4 backdrop-blur-sm shadow-xl`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-sm">An account is required</p>
                  <p className={`${themeStyles.secondaryText} text-xs`}>Create an account to get started</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Link
                  to="/signup"
                  onClick={() => setShowSignupBanner(false)}
                  className={`px-3 py-1 ${themeStyles.button} rounded-lg text-xs text-white shadow-md`}
                >
                  Sign Up
                </Link>
                <button
                  onClick={() => setShowSignupBanner(false)}
                  className={`p-1 ${themeStyles.hover} rounded`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Limit warning for free accounts */}
      {showLimitWarning && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50"
        >
          <div className={`bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4 backdrop-blur-sm shadow-xl`}>
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium">Free limit reached!</p>
                <p className={`text-sm ${themeStyles.secondaryText}`}>Upgrade to continue creating.</p>
              </div>
              <Link 
                to="/pricing" 
                onClick={() => setShowLimitWarning(false)}
                className={`ml-4 px-4 py-1 ${themeStyles.button} rounded-lg text-sm text-white shadow-md`}
              >
                Upgrade
              </Link>
            </div>
          </div>
        </motion.div>
      )}
      
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">
            <span className={`bg-gradient-to-r ${themeStyles.gradient} bg-clip-text text-transparent`}>
              Bible Art Studio
            </span>
          </h1>
          <p className={themeStyles.secondaryText}>
            Create beautiful biblical art and animations
            {!isAuthenticated && (
              <span className="block mt-1 text-amber-600 text-sm">
                An account is required
              </span>
            )}
            {isAuthenticated && user?.plan !== 'paid' && (
              <span className="block mt-1 text-amber-600 text-sm">
                Free: 1 creation available (image OR video)
              </span>
            )}
            {isAuthenticated && user?.plan === 'paid' && (
              <span className="block mt-1 text-amber-600 text-sm">
                <Crown className="w-3 h-3 inline mr-1" />
                Pro: Unlimited creations
              </span>
            )}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className={`inline-flex ${themeStyles.secondaryBg} rounded-xl p-1 shadow-md`}>
            <button
              onClick={() => setActiveTab('image')}
              className={`px-6 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                activeTab === 'image'
                  ? `${themeStyles.button} text-white shadow-md`
                  : `${themeStyles.secondaryText} hover:${themeStyles.text}`
              }`}
            >
              <Image className="w-4 h-4" />
              <span>Text to Image</span>
            </button>
            <button
              onClick={() => setActiveTab('video')}
              className={`px-6 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                activeTab === 'video'
                  ? `${themeStyles.button} text-white shadow-md`
                  : `${themeStyles.secondaryText} hover:${themeStyles.text}`
              }`}
            >
              <Video className="w-4 h-4" />
              <span>Image to Video</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Panel - Controls */}
          <div className="lg:col-span-2">
            <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 shadow-md`}>
              {activeTab === 'image' ? (
                <>
                  <div className="mb-6">
                    <label className={`block text-sm font-medium mb-3 flex items-center`}>
                      <Feather className={`w-4 h-4 mr-2 ${themeStyles.accent}`} />
                      Image Prompt
                    </label>
                    <textarea
                      value={imagePrompt}
                      onChange={(e) => setImagePrompt(e.target.value)}
                      className={`w-full p-4 ${themeStyles.input} rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm min-h-[120px] ${themeStyles.text} shadow-inner`}
                      placeholder="Describe the biblical scene you want to create..."
                      rows="4"
                      disabled={!isAuthenticated}
                    />
                    {!isAuthenticated && (
                      <div className="text-xs text-amber-600 mt-2">
                        Please sign up or log in to create art
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className={`block text-sm font-medium mb-3`}>Art Style</label>
                      <div className="grid grid-cols-3 gap-2">
                        {styles.map((style) => (
                          <button
                            key={style.id}
                            onClick={() => setImageStyle(style.id)}
                            className={`p-3 rounded-xl transition-all text-sm ${
                              imageStyle === style.id
                                ? `${themeStyles.accentBg} border ${themeStyles.accentBorder} shadow-md`
                                : `${themeStyles.secondaryBg} ${themeStyles.hover} border ${themeStyles.border}`
                            } ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={!isAuthenticated}
                          >
                            <div className="text-lg mb-1">{style.icon}</div>
                            <div className="text-xs">{style.name}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-3`}>Aspect Ratio</label>
                      <div className="grid grid-cols-2 gap-2">
                        {aspectRatios.map((ratio) => (
                          <button
                            key={ratio.id}
                            onClick={() => setAspectRatio(ratio.id)}
                            className={`p-3 rounded-xl transition-all text-sm ${
                              aspectRatio === ratio.id
                                ? `${themeStyles.accentBg} border ${themeStyles.accentBorder} shadow-md`
                                : `${themeStyles.secondaryBg} ${themeStyles.hover} border ${themeStyles.border}`
                            } ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={!isAuthenticated}
                          >
                            <div className="text-lg mb-1">{ratio.icon}</div>
                            <div className="text-xs">{ratio.name}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={generateImage}
                    disabled={isGenerating || !imagePrompt.trim() || !isAuthenticated}
                    className={`w-full py-3 rounded-xl font-medium transition-colors mb-4 shadow-md hover:shadow-lg ${
                      isGenerating || !imagePrompt.trim() || !isAuthenticated
                        ? `${themeStyles.secondaryBg} cursor-not-allowed`
                        : `${themeStyles.button} text-white`
                    }`}
                  >
                    {isGenerating ? (
                      <span className="flex items-center justify-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Creating...</span>
                      </span>
                    ) : !isAuthenticated ? (
                      <span className="flex items-center justify-center space-x-2">
                        <LogIn className="w-5 h-5" />
                        <span>Sign In to Create</span>
                      </span>
                    ) : (
                      <span className="flex items-center justify-center space-x-2">
                        <Sparkles className="w-5 h-5" />
                        <span>Create Image</span>
                      </span>
                    )}
                  </button>
                  
                  {isAuthenticated && user?.plan !== 'paid' && (
                    <div className={`text-center text-sm ${themeStyles.secondaryText}`}>
                      Free creations remaining: {1 - freeUsage.image}/1
                    </div>
                  )}
                  {!isAuthenticated && (
                    <div className="text-center">
                      <Link
                        to="/signup"
                        className={`${themeStyles.accent} hover:underline text-sm font-medium`}
                      >
                        An account is required →
                      </Link>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-6">
                    <label className={`block text-sm font-medium mb-3 flex items-center`}>
                      <Film className={`w-4 h-4 mr-2 ${themeStyles.accent}`} />
                      Video Prompt
                    </label>
                    <textarea
                      value={videoPrompt}
                      onChange={(e) => setVideoPrompt(e.target.value)}
                      className={`w-full p-4 ${themeStyles.input} rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm min-h-[120px] ${themeStyles.text} shadow-inner`}
                      placeholder="Describe the animation or movement you want..."
                      rows="4"
                      disabled={!isAuthenticated}
                    />
                    {!isAuthenticated && (
                      <div className="text-xs text-amber-600 mt-2">
                        Please sign up or log in to create videos
                      </div>
                    )}
                  </div>

                  <div className="mb-6">
                    <label className={`block text-sm font-medium mb-3`}>Upload Image</label>
                    <div 
                      onClick={() => isAuthenticated && fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                        !isAuthenticated 
                          ? `${themeStyles.border} opacity-50 cursor-not-allowed` 
                          : `${themeStyles.border} hover:border-amber-500`
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={!isAuthenticated}
                      />
                      {previewImage ? (
                        <div className="relative">
                          <img
                            src={previewImage}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg shadow-md"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              clearImage();
                            }}
                            className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-md"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <Upload className={`w-12 h-12 ${themeStyles.secondaryText} mx-auto mb-4`} />
                          <p className={`${themeStyles.secondaryText} mb-2`}>
                            {!isAuthenticated ? 'Sign in to upload images' : 'Click to upload an image'}
                          </p>
                          <p className={`${themeStyles.secondaryText} text-sm`}>or drag and drop</p>
                          <p className={`${themeStyles.secondaryText} text-sm mt-2`}>PNG, JPG, GIF up to 10MB</p>
                        </>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={generateVideo}
                    disabled={isGenerating || !videoPrompt.trim() || !selectedImage || !isAuthenticated}
                    className={`w-full py-3 rounded-xl font-medium transition-colors shadow-md hover:shadow-lg ${
                      isGenerating || !videoPrompt.trim() || !selectedImage || !isAuthenticated
                        ? `${themeStyles.secondaryBg} cursor-not-allowed`
                        : `${themeStyles.button} text-white`
                    }`}
                  >
                    {isGenerating ? (
                      <span className="flex items-center justify-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Creating Video...</span>
                      </span>
                    ) : !isAuthenticated ? (
                      <span className="flex items-center justify-center space-x-2">
                        <LogIn className="w-5 h-5" />
                        <span>Sign In to Create</span>
                      </span>
                    ) : (
                      <span className="flex items-center justify-center space-x-2">
                        <Film className="w-5 h-5" />
                        <span>Create Video</span>
                      </span>
                    )}
                  </button>
                  
                  {isAuthenticated && user?.plan !== 'paid' && (
                    <div className={`text-center text-sm ${themeStyles.secondaryText} mt-4`}>
                      Free creations remaining: {1 - freeUsage.video}/1
                    </div>
                  )}
                  {!isAuthenticated && (
                    <div className="text-center mt-4">
                      <Link
                        to="/signup"
                        className={`${themeStyles.accent} hover:underline text-sm font-medium`}
                      >
                        An account is required →
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Output Preview */}
          <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 shadow-md`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium flex items-center">
                <DoveIcon className={`w-4 h-4 mr-2 ${themeStyles.accent}`} />
                Generated Result
              </h3>
              {(generatedImage || generatedVideo) && (
                <button
                  onClick={clearOutput}
                  className={`text-xs ${themeStyles.secondaryText} hover:${themeStyles.text}`}
                >
                  Clear
                </button>
              )}
            </div>
            
            {generatedImage && isAuthenticated && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-600">Image created!</p>
                      <p className={`text-sm ${themeStyles.secondaryText}`}>Saved to your gallery</p>
                    </div>
                  </div>
                  <Link
                    to="/images"
                    className={`px-4 py-2 ${themeStyles.secondaryBg} ${themeStyles.hover} rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 border ${themeStyles.border} shadow-sm`}
                  >
                    <Image className={`w-4 h-4 ${themeStyles.accent}`} />
                    <span>View Gallery</span>
                  </Link>
                </div>
              </div>
            )}

            {generatedVideo && isAuthenticated && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-600">Video created!</p>
                      <p className={`text-sm ${themeStyles.secondaryText}`}>Saved to your gallery</p>
                    </div>
                  </div>
                  <Link
                    to="/videos"
                    className={`px-4 py-2 ${themeStyles.secondaryBg} ${themeStyles.hover} rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 border ${themeStyles.border} shadow-sm`}
                  >
                    <Film className={`w-4 h-4 ${themeStyles.accent}`} />
                    <span>View Gallery</span>
                  </Link>
                </div>
              </div>
            )}
            
            <div className={`aspect-square ${themeStyles.secondaryBg} rounded-xl border ${themeStyles.border} flex items-center justify-center overflow-hidden shadow-inner`}>
              {activeTab === 'image' ? (
                generatedImage ? (
                  <div className="relative w-full h-full">
                    <img
                      src={generatedImage}
                      alt="Generated"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-3 right-3 flex space-x-2">
                      <button
                        onClick={downloadImage}
                        className={`p-2 ${themeStyles.secondaryBg} ${themeStyles.hover} rounded-lg transition-colors shadow-md`}
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigator.clipboard.writeText(generatedImage)}
                        className={`p-2 ${themeStyles.secondaryBg} ${themeStyles.hover} rounded-lg transition-colors shadow-md`}
                        title="Copy URL"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-8">
                    <Image className={`w-12 h-12 ${themeStyles.secondaryText} mx-auto mb-4`} />
                    <p className={`${themeStyles.secondaryText} text-sm`}>No image created yet</p>
                    <p className={`${themeStyles.secondaryText} text-xs mt-1`}>Your creation will appear here</p>
                  </div>
                )
              ) : (
                generatedVideo ? (
                  <div className="relative w-full h-full">
                    <video
                      src={generatedVideo}
                      controls
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-3 right-3">
                      <button
                        onClick={downloadVideo}
                        className={`p-2 ${themeStyles.secondaryBg} ${themeStyles.hover} rounded-lg transition-colors shadow-md`}
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-8">
                    <Video className={`w-12 h-12 ${themeStyles.secondaryText} mx-auto mb-4`} />
                    <p className={`${themeStyles.secondaryText} text-sm`}>No video created yet</p>
                    <p className={`${themeStyles.secondaryText} text-xs mt-1`}>Your creation will appear here</p>
                  </div>
                )
              )}
            </div>
            
            {(generatedImage || generatedVideo) && (
              <div className="mt-4 text-sm">
                <div className={`flex items-center justify-between ${themeStyles.secondaryText}`}>
                  <span>Status:</span>
                  <span className="text-green-600 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Ready
                  </span>
                </div>
                {isAuthenticated && user?.plan !== 'paid' && activeTab === 'image' && (
                  <div className="mt-2 text-amber-600">
                    <span className="flex items-center">
                      <Info className="w-3 h-3 mr-1" />
                      You've used your free creation
                    </span>
                  </div>
                )}
                {isAuthenticated && user?.plan !== 'paid' && activeTab === 'video' && (
                  <div className="mt-2 text-amber-600">
                    <span className="flex items-center">
                      <Info className="w-3 h-3 mr-1" />
                      You've used your free creation
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Examples & Usage */}
          <div className="space-y-8">
            {/* Example Prompts */}
            <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 shadow-md`}>
              <h3 className="text-sm font-medium mb-4 flex items-center">
                <Sparkles className={`w-4 h-4 mr-2 ${themeStyles.accent}`} />
                Example Prompts
              </h3>
              <div className="space-y-3">
                {examplePrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => copyPrompt(prompt)}
                    className={`w-full text-left p-3 ${themeStyles.secondaryBg} ${themeStyles.hover} rounded-xl transition-all group border ${themeStyles.border}`}
                  >
                    <div className="text-sm line-clamp-2">{prompt}</div>
                    <div className={`text-xs ${themeStyles.secondaryText} mt-1 group-hover:${themeStyles.accent}`}>
                      Click to use
                    </div>
                  </button>
                ))}
              </div>
              {!isAuthenticated && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-xs text-amber-600">
                    <Info className="w-3 h-3 inline mr-1" />
                    Sign up to use these prompts
                  </p>
                </div>
              )}
            </div>
            
            {/* Usage Stats */}
            <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 shadow-md`}>
              <h3 className="text-sm font-medium mb-4 flex items-center">
                <BarChart3 className={`w-4 h-4 mr-2 ${themeStyles.accent}`} />
                Usage Summary
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${themeStyles.secondaryText}`}>Account:</span>
                  <span className={`text-sm font-medium ${
                    !isAuthenticated ? 'text-amber-600' : 
                    user?.plan === 'paid' ? 'text-green-600' : 'text-amber-600'
                  }`}>
                    {!isAuthenticated ? 'Guest' : 
                     user?.plan === 'paid' ? 'Pro' : 'Free'}
                  </span>
                </div>
                
                {isAuthenticated && user?.plan !== 'paid' && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className={`text-sm ${themeStyles.secondaryText}`}>Images:</span>
                      <span className="text-sm">{freeUsage.image}/1</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className={`text-sm ${themeStyles.secondaryText}`}>Videos:</span>
                      <span className="text-sm">{freeUsage.video}/1</span>
                    </div>
                    <div className="mt-4 pt-3 border-t ${themeStyles.border}">
                      <Link
                        to="/pricing"
                        className={`block text-center px-4 py-2 ${themeStyles.button} text-white rounded-xl text-sm font-medium transition-colors shadow-md hover:shadow-lg`}
                      >
                        Upgrade to Unlimited
                      </Link>
                    </div>
                  </>
                )}
                
                {!isAuthenticated && (
                  <div className="mt-4 space-y-3">
                    <Link
                      to="/signup"
                      className={`block text-center px-4 py-2 ${themeStyles.button} text-white rounded-xl text-sm font-medium transition-colors shadow-md hover:shadow-lg`}
                    >
                      Sign Up for Free
                    </Link>
                    <Link
                      to="/login"
                      className={`block text-center px-4 py-2 ${themeStyles.secondaryBg} border ${themeStyles.border} rounded-xl text-sm font-medium transition-colors shadow-sm ${themeStyles.hover}`}
                    >
                      Log In
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Simplified Home Page - Bible-themed
// Enhanced HomePage with Typing Animation and Modern Design
function HomePage({ isAuthenticated, user }) {
  const { themeStyles } = React.useContext(ThemeContext);
  const [typedText, setTypedText] = useState('');
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showBibleVerse, setShowBibleVerse] = useState({ verse: '', reference: '' });
  const [featuredPost, setFeaturedPost] = useState(null);
  const [stats, setStats] = useState({ verses: 0, users: 0, images: 0 });
  
  const phrases = [
    "Ask me about Scripture...",
    "Explore God's Word with me...",
    "Discover biblical wisdom...",
    "Let's study together...",
    "Find peace in His promises..."
  ];

  const bibleVerses = [
    { verse: "Your word is a lamp to my feet and a light to my path.", reference: "Psalm 119:105" },
    { verse: "I can do all things through Christ who strengthens me.", reference: "Philippians 4:13" },
    { verse: "For God so loved the world that He gave His only Son.", reference: "John 3:16" },
    { verse: "Be strong and courageous. Do not be afraid.", reference: "Joshua 1:9" },
    { verse: "The Lord is my shepherd; I shall not want.", reference: "Psalm 23:1" },
    { verse: "Cast all your anxiety on Him because He cares for you.", reference: "1 Peter 5:7" }
  ];

  // Typing animation effect
  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timeout;
    
    if (isDeleting) {
      timeout = setTimeout(() => {
        setTypedText(currentPhrase.substring(0, typedText.length - 1));
        if (typedText.length === 0) {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
        }
      }, 50);
    } else {
      timeout = setTimeout(() => {
        setTypedText(currentPhrase.substring(0, typedText.length + 1));
        if (typedText.length === currentPhrase.length) {
          timeout = setTimeout(() => setIsDeleting(true), 2000);
        }
      }, 100);
    }
    
    return () => clearTimeout(timeout);
  }, [typedText, currentPhraseIndex, isDeleting]);

  // Rotating Bible verse effect
  useEffect(() => {
    const interval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * bibleVerses.length);
      setShowBibleVerse(bibleVerses[randomIndex]);
    }, 8000);
    
    // Set initial verse
    setShowBibleVerse(bibleVerses[0]);
    
    return () => clearInterval(interval);
  }, []);

  // Fetch stats for animation
  useEffect(() => {
    const fetchStats = async () => {
      try {
        // You can replace these with actual API calls
        setStats({
          verses: 31102, // Number of Bible verses
          users: Math.floor(Math.random() * 1000) + 500,
          images: Math.floor(Math.random() * 5000) + 1000
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    
    fetchStats();
    
    // Animate stats counting
    const interval = setInterval(() => {
      setStats(prev => ({
        verses: prev.verses,
        users: prev.users < 5000 ? prev.users + Math.floor(Math.random() * 10) : prev.users,
        images: prev.images < 20000 ? prev.images + Math.floor(Math.random() * 50) : prev.images
      }));
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  // Fetch featured blog post
  useEffect(() => {
    const fetchFeaturedPost = async () => {
      try {
        const response = await axios.get(`${API_URL}/blog/posts?limit=1`);
        if (response.data.posts && response.data.posts.length > 0) {
          setFeaturedPost(response.data.posts[0]);
        }
      } catch (error) {
        console.error('Failed to fetch featured post:', error);
      }
    };
    fetchFeaturedPost();
  }, []);

  if (isAuthenticated && user?.plan === 'paid') {
    return <Navigate to="/chat" />;
  }

  return (
    <div className={`min-h-screen ${themeStyles.background}`}>
      
      {/* Animated Background Gradient */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-amber-300/20 blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-orange-300/20 blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Hero Section with Typing Animation */}
      <div className="relative max-w-7xl mx-auto px-4 pt-20 pb-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className={`w-28 h-28 rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300`}>
              <img 
                src="https://app.gospelflixstream.com/bibleai.png" 
                alt="Bible AI Logo"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Main Title */}
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            <span className={`bg-gradient-to-r ${themeStyles.gradient} bg-clip-text text-transparent`}>
              Bible AI
            </span>
          </h1>
          
          {/* Typing Animation */}
          <div className="h-20 mb-8">
            <p className="text-xl md:text-2xl text-amber-600 dark:text-amber-400 font-medium">
              <span className="inline-block border-r-4 border-amber-500 pr-1 animate-pulse">
                {typedText}
              </span>
            </p>
          </div>
          
          {/* Description */}
          <p className={`text-lg md:text-xl ${themeStyles.secondaryText} max-w-2xl mx-auto mb-8 leading-relaxed`}>
            Your AI-powered companion for exploring Scripture, deepening your faith, 
            and discovering the timeless wisdom of God's Word.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/chat"
                className={`inline-flex items-center px-8 py-4 ${themeStyles.button} text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl text-lg`}
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Start Chatting Free
              </Link>
            </motion.div>
            
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/pricing"
                className={`inline-flex items-center px-8 py-4 ${themeStyles.secondaryBg} border ${themeStyles.border} rounded-xl font-semibold transition-all shadow-md hover:shadow-lg text-lg`}
              >
                <Crown className="w-5 h-5 mr-2 text-amber-500" />
                Upgrade to Pro
              </Link>
            </motion.div>
          </div>
        </motion.div>

        {/* Animated Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
        >
          <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 text-center shadow-md hover:shadow-lg transition-all`}>
            <div className="text-4xl font-bold text-amber-600 mb-2 animate-count">
              {stats.verses.toLocaleString()}+
            </div>
            <div className={`text-sm ${themeStyles.secondaryText}`}>Bible Verses</div>
            <div className="mt-2 text-xs text-amber-500">In our database</div>
          </div>
          
          <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 text-center shadow-md hover:shadow-lg transition-all`}>
            <div className="text-4xl font-bold text-amber-600 mb-2 animate-count">
              {stats.users.toLocaleString()}+
            </div>
            <div className={`text-sm ${themeStyles.secondaryText}`}>Active Users</div>
            <div className="mt-2 text-xs text-amber-500">Growing daily</div>
          </div>
          
          <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 text-center shadow-md hover:shadow-lg transition-all`}>
            <div className="text-4xl font-bold text-amber-600 mb-2 animate-count">
              {stats.images.toLocaleString()}+
            </div>
            <div className={`text-sm ${themeStyles.secondaryText}`}>AI Creations</div>
            <div className="mt-2 text-xs text-amber-500">Biblical art & videos</div>
          </div>
        </motion.div>

        {/* Rotating Bible Verse */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className={`mb-16 p-8 ${themeStyles.highlight} rounded-2xl border ${themeStyles.border} shadow-lg`}
        >
          <div className="text-center">
            <div className="text-4xl mb-4">✝️</div>
            <p className="text-xl md:text-2xl italic font-medium mb-3">
              "{showBibleVerse.verse}"
            </p>
            <p className={`text-sm ${themeStyles.accent} font-semibold`}>
              — {showBibleVerse.reference} —
            </p>
          </div>
        </motion.div>

        {/* Features Grid with Animations */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
        >
          <motion.div whileHover={{ y: -5 }} className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-8 shadow-md hover:shadow-xl transition-all`}>
            <div className={`w-16 h-16 rounded-xl ${themeStyles.accentBg} flex items-center justify-center mb-6 mx-auto`}>
              <MessageSquare className={`w-8 h-8 ${themeStyles.accent}`} />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-center">Biblical Chat</h3>
            <p className={`${themeStyles.secondaryText} text-center leading-relaxed`}>
              Ask questions, explore Scripture, and receive thoughtful, biblically-grounded answers 24/7.
            </p>
          </motion.div>

          <motion.div whileHover={{ y: -5 }} className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-8 shadow-md hover:shadow-xl transition-all`}>
            <div className={`w-16 h-16 rounded-xl ${themeStyles.accentBg} flex items-center justify-center mb-6 mx-auto`}>
              <Image className={`w-8 h-8 ${themeStyles.accent}`} />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-center">Sacred Art</h3>
            <p className={`${themeStyles.secondaryText} text-center leading-relaxed`}>
              Create beautiful biblical scenes, illustrations, and artwork inspired by Scripture.
            </p>
          </motion.div>

          <motion.div whileHover={{ y: -5 }} className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-8 shadow-md hover:shadow-xl transition-all`}>
            <div className={`w-16 h-16 rounded-xl ${themeStyles.accentBg} flex items-center justify-center mb-6 mx-auto`}>
              <Film className={`w-8 h-8 ${themeStyles.accent}`} />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-center">Bible Stories</h3>
            <p className={`${themeStyles.secondaryText} text-center leading-relaxed`}>
              Bring Scripture to life with AI-generated videos and animated Bible stories.
            </p>
          </motion.div>
        </motion.div>

        {/* Featured Blog Post */}
        {featuredPost && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="mb-16"
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2 flex items-center justify-center">
                <BookOpen className={`w-5 h-5 mr-2 ${themeStyles.accent}`} />
                Latest from Our Blog
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-amber-400 to-orange-400 mx-auto rounded-full"></div>
            </div>
            
            <Link to={`/blog/${featuredPost.slug}`}>
              <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 hover:shadow-xl transition-all cursor-pointer`}>
                <h3 className="text-xl font-bold mb-3 hover:text-amber-600 transition-colors">
                  {featuredPost.title}
                </h3>
                <p className={`${themeStyles.secondaryText} mb-4 line-clamp-3`}>
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${themeStyles.accent} font-medium flex items-center`}>
                    Read Full Article
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </span>
                  <span className={`text-xs ${themeStyles.secondaryText}`}>
                    {new Date(featuredPost.published_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        )}

        {/* Testimonials Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2 flex items-center justify-center">
              <Heart className={`w-5 h-5 mr-2 ${themeStyles.accent}`} />
              What Our Users Say
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-amber-400 to-orange-400 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 shadow-md`}>
              <p className={`${themeStyles.secondaryText} italic mb-4`}>
                "Bible AI has transformed my daily devotions. I can ask questions and get thoughtful, 
                biblically-grounded answers anytime."
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mr-3">
                  <User className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="font-semibold">Sarah M.</div>
                  <div className="text-xs text-amber-500">Bible Study Leader</div>
                </div>
              </div>
            </div>
            
            <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 shadow-md`}>
              <p className={`${themeStyles.secondaryText} italic mb-4`}>
                "The image generation feature is incredible! I've created beautiful artwork for our 
                church's children's ministry."
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mr-3">
                  <User className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="font-semibold">Pastor David</div>
                  <div className="text-xs text-amber-500">Youth Pastor</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Call to Action Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className={`bg-gradient-to-r ${themeStyles.gradient} rounded-2xl p-8 text-white shadow-xl`}
        >
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-3">Ready to Deepen Your Faith?</h3>
            <p className="mb-6 opacity-95">Join thousands of believers exploring Scripture with AI assistance.</p>
            <Link
              to="/signup"
              className="inline-flex items-center px-6 py-3 bg-white text-amber-600 rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Create Free Account
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// Simplified Login Page - Bible-themed
function LoginPage({ login }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const { themeStyles } = React.useContext(ThemeContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResendMessage('');
    setShowResendButton(false);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });
      
      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        window.location.href = '/chat';
      }
    } catch (err) {
      const errorData = err.response?.data;
      
      if (errorData?.error === 'EMAIL_NOT_VERIFIED') {
        setError('Please verify your email address before logging in. We have sent you a link.');
        setShowResendButton(true);
        setResendMessage('Check your email for the verification link. If you cant see it check your Spam folder');
      } else {
        setError(errorData?.error || 'An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/resend-verification`, {
        email
      });
      
      if (response.data.success) {
        setResendMessage('Verification email has been resent.');
        setShowResendButton(false);
        setError('');
      }
    } catch (err) {
      const errorData = err.response?.data;
      setResendMessage(errorData?.error || 'Failed to resend verification email');
    }
  };

  return (
    <div className={`min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 ${themeStyles.background}`}>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md"
      >
        <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-8 shadow-xl`}>
         <div className="text-center mb-8">
  <div className={`w-16 h-16 rounded-xl overflow-hidden mx-auto mb-4 shadow-md`}>
    <img 
      src="https://app.gospelflixstream.com/bibleai.png" 
      alt="Bible AI"
      className="w-full h-full object-cover"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = 'https://via.placeholder.com/64/amber-600/white?text=Bible';
      }}
    />
  </div>
  <h2 className="text-2xl font-bold mb-2">Welcome to Bible AI</h2>
  <p className={`${themeStyles.secondaryText}`}>Sign in to continue your journey</p>
</div>

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {resendMessage && (
            <div className={`mb-6 p-3 rounded-lg border text-sm ${
              resendMessage.includes('resent') 
                ? 'bg-green-50 border-green-200 text-green-600' 
                : 'bg-amber-50 border-amber-200 text-amber-600'
            }`}>
              {resendMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full p-3 ${themeStyles.input} rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm ${themeStyles.text} shadow-inner`}
                  placeholder="you@example.com"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full p-3 ${themeStyles.input} rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm ${themeStyles.text} shadow-inner`}
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
              </div>

              {showResendButton && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={isLoading}
                    className={`text-sm ${themeStyles.accent} hover:underline font-medium`}
                  >
                    Resend verification email
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 rounded-xl font-medium transition-colors shadow-md hover:shadow-lg ${
                  isLoading
                    ? `${themeStyles.secondaryBg} cursor-not-allowed`
                    : `${themeStyles.button} text-white`
                }`}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </div>
          </form>

          <div className="mt-8 pt-8 border-t ${themeStyles.border}">
            <div className="space-y-4">
              <div className="text-center">
                <p className={`${themeStyles.secondaryText} text-sm`}>
                  Don't have an account?{' '}
                  <Link to="/signup" className={`${themeStyles.accent} hover:underline font-medium`}>
                    Create one
                  </Link>
                </p>
              </div>
              
              <div className="text-center">
                <p className={`${themeStyles.secondaryText} text-sm`}>
                  Or continue as{' '}
                  <Link to="/chat" className={`${themeStyles.accent} hover:underline font-medium`}>
                    Guest
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Signup Page Component - Bible-themed
// Updated SignupPage Component - NO VERIFICATION
function SignupPage({ login }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { themeStyles } = React.useContext(ThemeContext);

  const validateForm = () => {
    if (!email.trim()) {
      setError('Email is required');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) return;
    
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/register`, {
        email,
        password
      });
      
      if (response.data.success) {
        setSuccess(true);
        
        // Auto-login after registration
        setTimeout(async () => {
          try {
            const loginResult = await login(email, password);
            if (loginResult.success) {
              navigate('/chat');
            } else {
              navigate('/login');
            }
          } catch (err) {
            setError('Registration successful but auto-login failed.');
            setSuccess(false);
          }
        }, 1500);
      } else {
        setError(response.data.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className={`min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 ${themeStyles.background}`}>
        <div className="w-full max-w-md text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-8 shadow-xl`}
          >
            <div className={`w-16 h-16 rounded-xl bg-green-100 border border-green-200 flex items-center justify-center mx-auto mb-4`}>
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-4">Welcome!</h2>
            <p className={`${themeStyles.secondaryText} mb-4`}>Your account has been created successfully.</p>
            <div className="flex space-x-1 justify-center">
              <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`}></div>
              <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.1s' }}></div>
              <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.2s' }}></div>
            </div>
            <p className={`text-sm ${themeStyles.secondaryText} mt-4`}>Logging you in...</p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 ${themeStyles.background}`}>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md"
      >
        <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-8 shadow-xl`}>
          <div className="text-center mb-8">
            <div className={`w-16 h-16 rounded-xl overflow-hidden mx-auto mb-4 shadow-md`}>
              <img 
                src="https://app.gospelflixstream.com/bibleai.png" 
                alt="Bible AI"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/64/amber-600/white?text=Bible';
                }}
              />
            </div>
            <h2 className="text-2xl font-bold mb-2">Create Account</h2>
            <p className={`${themeStyles.secondaryText}`}>Join us in exploring faith</p>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full p-3 ${themeStyles.input} rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm ${themeStyles.text} shadow-inner`}
                  placeholder="you@example.com"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full p-3 ${themeStyles.input} rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm ${themeStyles.text} shadow-inner`}
                  placeholder="••••••••"
                  required
                  minLength="6"
                  disabled={isLoading}
                />
                <p className={`text-xs ${themeStyles.secondaryText} mt-1`}>Minimum 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full p-3 ${themeStyles.input} rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm ${themeStyles.text} shadow-inner`}
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 rounded-xl font-medium transition-colors shadow-md hover:shadow-lg ${
                  isLoading
                    ? `${themeStyles.secondaryBg} cursor-not-allowed`
                    : `${themeStyles.button} text-white`
                }`}
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </form>

          <div className="mt-8 pt-8 border-t ${themeStyles.border}">
            <div className="space-y-4">
              <div className="text-center">
                <p className={`${themeStyles.secondaryText} text-sm`}>
                  Already have an account?{' '}
                  <Link to="/login" className={`${themeStyles.accent} hover:underline font-medium`}>
                    Sign in
                  </Link>
                </p>
              </div>
              
              <div className="text-center">
                <p className={`${themeStyles.secondaryText} text-sm`}>
                  Or continue as{' '}
                  <Link to="/chat" className={`${themeStyles.accent} hover:underline font-medium`}>
                    Guest
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// PayStack Payment Component
// PayStack Payment Component - FIXED
function PayStackPayment({ onSuccess, onClose, amount, currency }) {
  const [loading, setLoading] = useState(false);
  
  const initializePayment = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please login first');
        onClose();
        return;
      }
      
      const response = await axios.post(
        `${API_URL}/payment/paystack-initiate`,
        { currency: currency },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.access_code) {
        // Get user email
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user || !user.email) {
          alert('User information not found. Please login again.');
          onClose();
          return;
        }
        
        // Use public key from backend response (from .env)
        const publicKey = response.data.public_key;
        
        if (!publicKey) {
          console.error('No public key received from backend');
          alert('Payment configuration error. Please contact support.');
          onClose();
          return;
        }
        
        // FIXED: Set amount correctly for PayStack
        let amountForPaystack;
        if (currency === 'KES') {
          amountForPaystack = 200000; // KES 2000 (actual amount, NOT multiplied by 100)
        } else {
          amountForPaystack = 1500; // $15 USD (1500 cents = $15.00)
        }
        
        console.log(`Initializing PayStack with amount: ${amountForPaystack} ${currency}`);
        
        // Open PayStack inline popup with key from backend
        const handler = window.PaystackPop.setup({
          key: publicKey,
          email: user.email,
          amount: amountForPaystack,
          currency: currency,
          ref: response.data.reference,
          callback: async (paymentResponse) => {
            console.log('Payment callback:', paymentResponse);
            
            // Show processing message
            const processingMsg = document.createElement('div');
            processingMsg.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
            processingMsg.innerHTML = `
              <div class="bg-white rounded-xl p-6 text-center max-w-md mx-4">
                <div class="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p class="font-semibold text-gray-800">Verifying payment...</p>
                <p class="text-sm text-gray-600 mt-2">Please wait while we upgrade your account</p>
              </div>
            `;
            document.body.appendChild(processingMsg);
            
            try {
              // Verify payment on backend
              const verifyRes = await axios.post(`${API_URL}/payment/paystack-verify`, {
                reference: paymentResponse.reference,
                planId: 'lifetime'
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              
              if (verifyRes.data.success) {
                // Update local storage with new user data
                if (verifyRes.data.user) {
                  localStorage.setItem('user', JSON.stringify(verifyRes.data.user));
                }
                
                // Show success message
                processingMsg.innerHTML = `
                  <div class="bg-white rounded-xl p-6 text-center max-w-md mx-4">
                    <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <p class="font-semibold text-green-600">Payment Successful!</p>
                    <p class="text-sm text-gray-600 mt-2">You now have Lifetime Pro access!</p>
                    <p class="text-xs text-gray-500 mt-4">Closing...</p>
                  </div>
                `;
                
                setTimeout(() => {
                  processingMsg.remove();
                  onSuccess();
                }, 2000);
              } else {
                throw new Error(verifyRes.data.error || 'Verification failed');
              }
            } catch (error) {
              console.error('Verification error:', error);
              processingMsg.innerHTML = `
                <div class="bg-white rounded-xl p-6 text-center max-w-md mx-4">
                  <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </div>
                  <p class="font-semibold text-red-600">Verification Failed</p>
                  <p class="text-sm text-gray-600 mt-2">${error.message}</p>
                  <button onclick="window.location.reload()" class="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm">
                    Try Again
                  </button>
                </div>
              `;
            }
          },
          onClose: () => {
            console.log('Payment window closed');
            setLoading(false);
            onClose();
          },
          onError: (error) => {
            console.error('Payment error:', error);
            alert('Payment failed. Please try again.');
            setLoading(false);
            onClose();
          }
        });
        
        handler.open();
      } else {
        alert(response.data.error || 'Failed to initialize payment');
        setLoading(false);
        onClose();
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(error.response?.data?.error || 'Payment failed. Please try again.');
      setLoading(false);
      onClose();
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="text-center">
          <div className="text-6xl mb-4">💳</div>
          <h2 className="text-2xl font-bold mb-3">Complete Payment</h2>
          <p className="text-gray-600 mb-6">
            Amount: <strong>{currency === 'KES' ? 'KES 2,000' : '$15.00 USD'}</strong>
          </p>
          <button
            onClick={initializePayment}
            disabled={loading}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Processing...
              </span>
            ) : (
              `Pay ${currency === 'KES' ? 'KES 2,000' : '$15.00 USD'}`
            )}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="w-full mt-3 text-gray-500 hover:text-gray-700 py-2 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Simplified Profile Page - Bible-themed
function ProfilePage({ user, logout }) {
  const isPaidUser = user?.plan === 'paid';
  const { themeStyles } = React.useContext(ThemeContext);

  return (
    <div className={`min-h-[calc(100vh-4rem)] p-4 ${themeStyles.background}`}>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold mb-8 flex items-center">
          <User className={`w-6 h-6 mr-2 ${themeStyles.accent}`} />
          Profile
        </h1>

        <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 shadow-xl`}>
          <div className="flex items-center space-x-4 mb-6">
            <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${themeStyles.gradient} flex items-center justify-center shadow-md`}>
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">{user?.email}</h2>
              <div className={`inline-flex items-center px-3 py-1 rounded-lg text-sm mt-2 ${
                isPaidUser
                  ? 'bg-green-100 text-green-600 border border-green-200'
                  : `${themeStyles.accentBg} ${themeStyles.accent} border ${themeStyles.accentBorder}`
              }`}>
                {isPaidUser ? (
                  <>
                    <Crown className="w-3 h-3 mr-1" />
                    Lifetime Pro
                  </>
                ) : 'Free Plan'}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Link
              to="/images"
              className={`${themeStyles.secondaryBg} p-4 rounded-xl hover:${themeStyles.hover} transition-colors border ${themeStyles.border} shadow-sm`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <Image className={`w-5 h-5 ${themeStyles.accent}`} />
                <h3 className="font-medium">Images</h3>
              </div>
              <p className={`text-sm ${themeStyles.secondaryText}`}>View your gallery</p>
            </Link>
            
            <Link
              to="/videos"
              className={`${themeStyles.secondaryBg} p-4 rounded-xl hover:${themeStyles.hover} transition-colors border ${themeStyles.border} shadow-sm`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <Film className={`w-5 h-5 ${themeStyles.accent}`} />
                <h3 className="font-medium">Videos</h3>
              </div>
              <p className={`text-sm ${themeStyles.secondaryText}`}>View your creations</p>
            </Link>
            
            <Link
              to="/chat"
              className={`${themeStyles.secondaryBg} p-4 rounded-xl hover:${themeStyles.hover} transition-colors border ${themeStyles.border} shadow-sm`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <MessageSquare className={`w-5 h-5 ${themeStyles.accent}`} />
                <h3 className="font-medium">Chats</h3>
              </div>
              <p className={`text-sm ${themeStyles.secondaryText}`}>View history</p>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className={`${themeStyles.secondaryBg} p-4 rounded-xl border ${themeStyles.border}`}>
              <div className={`text-sm ${themeStyles.secondaryText} mb-1`}>Member Since</div>
              <div className="font-medium">
                {new Date(user?.created_at).toLocaleDateString()}
              </div>
            </div>
            {isPaidUser && (
              <div className={`${themeStyles.secondaryBg} p-4 rounded-xl border ${themeStyles.border}`}>
                <div className={`text-sm ${themeStyles.secondaryText} mb-1`}>Upgraded On</div>
                <div className="font-medium">
                  {new Date(user?.plan_purchased_at).toLocaleDateString()}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            {!isPaidUser && (
              <Link
                to="/pricing"
                className={`block w-full py-3 ${themeStyles.button} text-white rounded-xl text-center font-medium transition-colors shadow-md hover:shadow-lg`}
              >
                Upgrade to Pro
              </Link>
            )}
            <button
              onClick={logout}
              className={`w-full py-3 ${themeStyles.secondaryBg} border ${themeStyles.border} rounded-xl font-medium transition-colors shadow-sm hover:shadow-md`}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Privacy/Terms Page Component - Bible-themed
function PrivacyPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { themeStyles } = React.useContext(ThemeContext);

  useEffect(() => {
    fetch('/api/privacy')
      .then(res => res.json())
      .then(data => {
        setContent(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load privacy content:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className={`min-h-[calc(100vh-4rem)] flex items-center justify-center ${themeStyles.background}`}>
        <div className={`w-8 h-8 border-2 ${themeStyles.accent} border-t-transparent rounded-full animate-spin`}></div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className={`min-h-[calc(100vh-4rem)] flex items-center justify-center ${themeStyles.background}`}>
        <p className={themeStyles.secondaryText}>Failed to load content</p>
      </div>
    );
  }

  return (
    <div className={`min-h-[calc(100vh-4rem)] p-4 ${themeStyles.background}`}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">{content.title}</h1>
          <p className={themeStyles.secondaryText}>Last updated: {content.lastUpdated}</p>
        </div>

        <div className="space-y-12">
          {/* Privacy Policy */}
          <div>
            <h2 className="text-2xl font-bold mb-6 pb-2 border-b ${themeStyles.border} flex items-center">
              <Shield className={`w-6 h-6 mr-2 ${themeStyles.accent}`} />
              Privacy Policy
            </h2>
            <div className="space-y-6">
              {content.privacy.map((section, index) => (
                <div key={index} className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 shadow-md`}>
                  <h3 className="text-lg font-semibold mb-3">{section.title}</h3>
                  <div className={`${themeStyles.secondaryText} whitespace-pre-line text-sm leading-relaxed`}>
                    {section.content}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Terms of Service */}
          <div>
            <h2 className="text-2xl font-bold mb-6 pb-2 border-b ${themeStyles.border} flex items-center">
              <Book className={`w-6 h-6 mr-2 ${themeStyles.accent}`} />
              Terms of Service
            </h2>
            <div className="space-y-6">
              {content.terms.map((section, index) => (
                <div key={index} className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 shadow-md`}>
                  <h3 className="text-lg font-semibold mb-3">{section.title}</h3>
                  <div className={`${themeStyles.secondaryText} whitespace-pre-line text-sm leading-relaxed`}>
                    {section.content}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 shadow-md`}>
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Mail className={`w-5 h-5 mr-2 ${themeStyles.accent}`} />
              Contact Information
            </h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <Mail className={`w-5 h-5 ${themeStyles.accent} mr-3`} />
                <div>
                  <p className={`text-sm ${themeStyles.secondaryText}`}>General Inquiries</p>
                  <a 
                    href={`mailto:${content.contact.email}`}
                    className={`${themeStyles.accent} hover:underline`}
                  >
                    {content.contact.email}
                  </a>
                </div>
              </div>
              <div className="flex items-center">
                <Shield className={`w-5 h-5 ${themeStyles.accent} mr-3`} />
                <div>
                  <p className={`text-sm ${themeStyles.secondaryText}`}>Privacy Concerns</p>
                  <a 
                    href={`mailto:${content.contact.privacyEmail}`}
                    className={`${themeStyles.accent} hover:underline`}
                  >
                    {content.contact.privacyEmail}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Contact Page Component - Bible-themed
function ContactPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { themeStyles } = React.useContext(ThemeContext);

  useEffect(() => {
    fetch('/api/contact')
      .then(res => res.json())
      .then(data => {
        setContent(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load contact info:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className={`min-h-[calc(100vh-4rem)] flex items-center justify-center ${themeStyles.background}`}>
        <div className={`w-8 h-8 border-2 ${themeStyles.accent} border-t-transparent rounded-full animate-spin`}></div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className={`min-h-[calc(100vh-4rem)] flex items-center justify-center ${themeStyles.background}`}>
        <p className={themeStyles.secondaryText}>Failed to load contact information</p>
      </div>
    );
  }

  return (
    <div className={`min-h-[calc(100vh-4rem)] p-4 ${themeStyles.background}`}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold mb-2">{content.title}</h1>
          <p className={themeStyles.secondaryText}>{content.description}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {content.contactMethods.map((method, index) => (
            <div key={index} className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-6 shadow-md`}>
              <div className="flex items-start space-x-4">
                <div className={`w-12 h-12 rounded-xl ${themeStyles.accentBg} border ${themeStyles.accentBorder} flex items-center justify-center flex-shrink-0`}>
                  {method.type.includes('Support') ? (
                    <HelpCircle className={`w-6 h-6 ${themeStyles.accent}`} />
                  ) : method.type.includes('Privacy') ? (
                    <Shield className={`w-6 h-6 ${themeStyles.accent}`} />
                  ) : method.type.includes('Business') ? (
                    <TrendingUp className={`w-6 h-6 ${themeStyles.accent}`} />
                  ) : (
                    <Mail className={`w-6 h-6 ${themeStyles.accent}`} />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold mb-2">{method.type}</h3>
                  <a 
                    href={`mailto:${method.email}`}
                    className={`${themeStyles.accent} hover:underline block mb-2`}
                  >
                    {method.email}
                  </a>
                  <p className={`text-sm ${themeStyles.secondaryText}`}>
                    Response time: {method.responseTime}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-8 text-center shadow-md`}>
          <h3 className="text-lg font-semibold mb-4">How to Reach Us</h3>
          <p className={`${themeStyles.secondaryText} mb-4`}>
            For the fastest response, please use the appropriate email address above.
          </p>
          <div className={`space-y-2 text-sm ${themeStyles.secondaryText}`}>
            <p>{content.hours}</p>
            <p>{content.note}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Simplified Pricing Page - Bible-themed
// Simplified Pricing Page - Bible-themed
function PricingPage({ user, isAuthenticated }) {
  const navigate = useNavigate();
  const { themeStyles } = React.useContext(ThemeContext);
  const [loading, setLoading] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: 'forever',
      features: [
        '5 AI Chat Messages',
        '1 Image Creation',
        '1 Video Creation',
        'Basic Support'
      ],
      cta: 'Current Plan'
    },
    {
      id: 'lifetime',
      name: 'Lifetime Pro',
      price: '$15',
      period: 'one-time',
      features: [
        'Unlimited Bible AI Chat',
        'Unlimited Biblical Image Creation',
        'Unlimited Image to Video Creation',
        'Priority Support',
        'Chat History',
        'All Future Features'
      ],
      cta: 'Get Lifetime Access',
      popular: true
    }
  ];

  const handlePayment = async (planId, currency) => {
    if (planId === 'free') {
      navigate('/chat');
      return;
    }

    setLoading(true);
    setProcessingPlan(planId);

    try {
      const token = localStorage.getItem('token');

      // Use the user prop from App.js instead of localStorage
      // This is the source of truth since it comes from React state
      if (!token || !isAuthenticated || !user) {
        alert('Please login first');
        navigate('/login');
        setLoading(false);
        setProcessingPlan(null);
        return;
      }

      console.log('=== Starting Payment Process ===');
      console.log('Plan ID:', planId);
      console.log('Currency:', currency);
      console.log('User Email:', user.email);

      // Initialize payment on backend - backend will generate a fresh reference
      const response = await axios.post(`${API_URL}/payment/paystack-initiate`, {
        email: user.email,
        currency: currency
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Full init response:', JSON.stringify(response.data, null, 2));

      // Check if response contains public_key
      if (!response.data.public_key) {
        console.error('ERROR: No public_key in response!');
        alert('Payment configuration error. Please contact support.');
        setLoading(false);
        setProcessingPlan(null);
        return;
      }

      console.log('Public key received:', response.data.public_key);

      if (response.data.success && response.data.access_code) {
        const paystackPublicKey = response.data.public_key;
        const currentReference = response.data.reference;

        console.log('Access code:', response.data.access_code);
        console.log('Reference (from PayStack):', currentReference);

        // Set amount for PayStack - USD only
        let amountForPaystack = 1500; // 1500 cents = $15.00 USD

        console.log(`Initializing PayStack with amount: ${amountForPaystack} ${currency}`);

        const paystackOptions = {
          key: paystackPublicKey,
          email: user.email,
          amount: amountForPaystack,
          currency: 'USD', // Force USD
          ref: currentReference,
          callback: async (paymentResponse) => {
            console.log('Payment callback:', paymentResponse);

            const processingMsg = document.createElement('div');
            processingMsg.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
            processingMsg.innerHTML = `
              <div class="bg-white rounded-xl p-6 text-center max-w-md mx-4">
                <div class="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p class="font-semibold text-gray-800">Verifying payment...</p>
                <p class="text-sm text-gray-600 mt-2">Please wait while we upgrade your account</p>
              </div>
            `;
            document.body.appendChild(processingMsg);

            try {
              const verifyResponse = await axios.post(`${API_URL}/payment/paystack-verify`, {
                reference: paymentResponse.reference || paymentResponse.trxref,
                planId: planId
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });

              console.log('Verify response:', verifyResponse.data);

              if (verifyResponse.data.success) {
                if (verifyResponse.data.user) {
                  localStorage.setItem('user', JSON.stringify(verifyResponse.data.user));
                }

                processingMsg.innerHTML = `
                  <div class="bg-white rounded-xl p-6 text-center max-w-md mx-4">
                    <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <p class="font-semibold text-green-600">Payment Successful!</p>
                    <p class="text-sm text-gray-600 mt-2">You now have Lifetime Pro access!</p>
                    <p class="text-xs text-gray-500 mt-4">Redirecting to chat...</p>
                  </div>
                `;

                setTimeout(() => {
                  processingMsg.remove();
                  window.location.href = '/chat';
                }, 2000);
              } else {
                throw new Error(verifyResponse.data.error || 'Verification failed');
              }
            } catch (error) {
              console.error('Verification error:', error);
              processingMsg.innerHTML = `
                <div class="bg-white rounded-xl p-6 text-center max-w-md mx-4">
                  <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </div>
                  <p class="font-semibold text-red-600">Verification Failed</p>
                  <p class="text-sm text-gray-600 mt-2">${error.message}</p>
                  <button onclick="window.location.href='/pricing'" class="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm">
                    Try Again
                  </button>
                </div>
              `;
            }
          },
          onClose: () => {
            console.log('Payment window closed');
            setLoading(false);
            setProcessingPlan(null);
          },
          onError: (error) => {
            console.error('Payment error:', error);
            alert('Payment failed. Please try again.');
            setLoading(false);
            setProcessingPlan(null);
          }
        };

        if (window.PaystackPop) {
          const paystack = new window.PaystackPop();
          paystack.newTransaction(paystackOptions);
        } else {
          const script = document.createElement('script');
          script.src = 'https://js.paystack.co/v1/inline.js';
          script.onload = () => {
            const paystack = new window.PaystackPop();
            paystack.newTransaction(paystackOptions);
          };
          document.body.appendChild(script);
        }
      } else {
        alert(response.data.error || 'Failed to initialize payment');
        setLoading(false);
        setProcessingPlan(null);
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(error.response?.data?.error || 'Payment failed. Please try again.');
      setLoading(false);
      setProcessingPlan(null);
    }
  };

  return (
    <div className={`min-h-[calc(100vh-4rem)] p-4 ${themeStyles.background}`}>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold mb-4">
            <span className={`bg-gradient-to-r ${themeStyles.gradient} bg-clip-text text-transparent`}>
              Simple, Transparent Pricing
            </span>
          </h1>
          <p className={themeStyles.secondaryText}>Choose the plan that's right for you</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`rounded-xl border ${
                plan.popular
                  ? `border-amber-500 ${themeStyles.card} shadow-xl`
                  : `${themeStyles.border} ${themeStyles.card} shadow-md`
              }`}
            >
              {plan.popular && (
                <div className={`px-4 py-2 bg-gradient-to-r ${themeStyles.gradient} text-white text-center rounded-t-xl font-medium`}>
                  MOST POPULAR
                </div>
              )}

              <div className="p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                  <div className="flex items-baseline mb-2">
                    <span className="text-3xl font-bold">{plan.price}</span>
                    <span className={`${themeStyles.secondaryText} ml-2`}>/{plan.period}</span>
                  </div>
                </div>

                <div className="mb-8">
                  <div className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-center text-sm">
                        <Check className={`w-4 h-4 text-green-600 mr-3`} />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {plan.id === 'free' ? (
                  <button
                    onClick={() => handlePayment(plan.id)}
                    className={`w-full py-3 rounded-xl font-medium transition-all shadow-md hover:shadow-lg ${
                      themeStyles.secondaryBg
                    } border ${themeStyles.border} hover:${themeStyles.hover}`}
                  >
                    {plan.cta}
                  </button>
                ) : (
                  <button
                    onClick={() => handlePayment(plan.id, 'USD')}
                    disabled={loading && processingPlan === plan.id}
                    className={`w-full py-3 rounded-xl font-medium transition-all shadow-md hover:shadow-lg ${
                      loading && processingPlan === plan.id
                        ? 'opacity-50 cursor-not-allowed'
                        : plan.popular
                        ? `${themeStyles.button} text-white`
                        : `${themeStyles.secondaryBg} border ${themeStyles.border} hover:${themeStyles.hover}`
                    }`}
                  >
                    {loading && processingPlan === plan.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </span>
                    ) : (
                      <span className="flex flex-col items-center">
                        <span>💳 Pay $15 USD (Lifetime)</span>
                        <span className="text-xs opacity-90">Secure payment via PayStack</span>
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-12 text-center ${themeStyles.secondaryText} text-sm`}>
          <p>All plans include access to Bible AI</p>
          <p className="text-xs mt-2">
            Secure payments via PayStack • Visa • Mastercard • Bank Transfer
          </p>
        </div>
      </div>
    </div>
  );
}

// Add this new component for PayPal success handling
function PaymentSuccessPage() {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing your payment...');
  const navigate = useNavigate();
  const location = useLocation();
  const { themeStyles } = React.useContext(ThemeContext);

  useEffect(() => {
    const processPayment = async () => {
      try {
        // Get query parameters
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        const PayerID = params.get('PayerID');
        const userId = params.get('user');

        // Get the stored order ID
        const orderId = localStorage.getItem('pendingOrder');
        
        if (!orderId || !token || !PayerID) {
          setStatus('error');
          setMessage('Missing payment information. Please contact support.');
          return;
        }

        // Get auth token
        const authToken = localStorage.getItem('token');
        if (!authToken) {
          setStatus('error');
          setMessage('You need to be logged in. Redirecting to login...');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Capture the payment
        const response = await axios.post(
          `${API_URL}/payment/capture-order`,
          { orderId },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (response.data.success) {
          setStatus('success');
          setMessage('Payment successful! You now have Lifetime Pro access!');
          
          // Update user data in localStorage
          if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }
          
          // Clear pending order
          localStorage.removeItem('pendingOrder');
          
          // Redirect to chat after 3 seconds
          setTimeout(() => navigate('/chat'), 3000);
        } else {
          setStatus('error');
          setMessage('Payment processing failed. Please contact support.');
        }
      } catch (error) {
        console.error('Payment processing error:', error);
        setStatus('error');
        setMessage(error.response?.data?.error || 'Payment processing failed. Please contact support.');
      }
    };

    processPayment();
  }, [location, navigate]);

  return (
    <div className={`min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 ${themeStyles.background}`}>
      <div className="w-full max-w-md text-center">
        <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-8 shadow-xl`}>
          {status === 'processing' && (
            <>
              <div className={`w-16 h-16 rounded-xl ${themeStyles.accentBg} border ${themeStyles.accentBorder} flex items-center justify-center mx-auto mb-4`}>
                <RefreshCw className={`w-8 h-8 ${themeStyles.accent} animate-spin`} />
              </div>
              <h2 className="text-2xl font-bold mb-4">Processing Payment</h2>
              <p className={themeStyles.secondaryText}>{message}</p>
              <div className="flex space-x-1 justify-center mt-6">
                <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`}></div>
                <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.1s' }}></div>
                <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.2s' }}></div>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-xl bg-green-100 border border-green-200 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-green-600">Payment Successful!</h2>
              <p className={themeStyles.secondaryText}>{message}</p>
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-600">
                  <Crown className="w-4 h-4 inline mr-1" />
                  You now have Lifetime Pro access!
                </p>
              </div>
              <p className={`text-sm ${themeStyles.secondaryText} mt-4`}>
                Redirecting you to chat...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-xl bg-red-100 border border-red-200 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-red-600">Payment Error</h2>
              <p className={themeStyles.secondaryText}>{message}</p>
              <div className="mt-6 space-y-3">
                <button
                  onClick={() => navigate('/pricing')}
                  className={`block w-full py-3 ${themeStyles.button} text-white rounded-xl font-medium transition-colors shadow-md`}
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className={`block w-full py-3 ${themeStyles.secondaryBg} border ${themeStyles.border} rounded-xl font-medium transition-colors shadow-sm`}
                >
                  Contact Support
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
// Payment Callback Handler Component
function PaymentCallbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { themeStyles } = React.useContext(ThemeContext);
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Verifying your payment...');

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get reference from URL
        const params = new URLSearchParams(location.search);
        const reference = params.get('reference') || params.get('trxref');
        
        console.log('Payment callback reference:', reference);
        
        if (!reference) {
          setStatus('error');
          setMessage('No payment reference found');
          return;
        }
        
        // Get auth token
        const token = localStorage.getItem('token');
        if (!token) {
          setStatus('error');
          setMessage('Please login to verify your payment');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }
        
        // Verify payment on backend
        const response = await axios.post(`${API_URL}/payment/paystack-verify`, {
          reference: reference,
          planId: 'lifetime'
        });
        
        console.log('Verification response:', response.data);
        
        if (response.data.success) {
          setStatus('success');
          setMessage('Payment successful! You now have Lifetime Pro access!');
          
          // Update user data in localStorage
          if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }
          
          // Redirect to chat after 3 seconds
          setTimeout(() => {
            navigate('/chat');
          }, 3000);
        } else {
          setStatus('error');
          setMessage(response.data.error || 'Payment verification failed');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('Payment verification failed. Please contact support.');
      }
    };
    
    verifyPayment();
  }, [location, navigate]);

  return (
    <div className={`min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 ${themeStyles.background}`}>
      <div className="w-full max-w-md text-center">
        <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-8 shadow-xl`}>
          {status === 'processing' && (
            <>
              <div className={`w-16 h-16 rounded-xl ${themeStyles.accentBg} border ${themeStyles.accentBorder} flex items-center justify-center mx-auto mb-4`}>
                <RefreshCw className={`w-8 h-8 ${themeStyles.accent} animate-spin`} />
              </div>
              <h2 className="text-2xl font-bold mb-4">Verifying Payment</h2>
              <p className={themeStyles.secondaryText}>{message}</p>
              <div className="flex space-x-1 justify-center mt-6">
                <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`}></div>
                <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.1s' }}></div>
                <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.2s' }}></div>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-xl bg-green-100 border border-green-200 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-green-600">Payment Successful!</h2>
              <p className={themeStyles.secondaryText}>{message}</p>
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-600">
                  <Crown className="w-4 h-4 inline mr-1" />
                  You now have Lifetime Pro access!
                </p>
              </div>
              <p className={`text-sm ${themeStyles.secondaryText} mt-4`}>
                Redirecting you to chat...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-xl bg-red-100 border border-red-200 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-red-600">Verification Error</h2>
              <p className={themeStyles.secondaryText}>{message}</p>
              <div className="mt-6 space-y-3">
                <button
                  onClick={() => navigate('/pricing')}
                  className={`block w-full py-3 ${themeStyles.button} text-white rounded-xl font-medium transition-colors shadow-md`}
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className={`block w-full py-3 ${themeStyles.secondaryBg} border ${themeStyles.border} rounded-xl font-medium transition-colors shadow-sm`}
                >
                  Contact Support
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
// Main App Component
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });
  const [anonymousUsage, setAnonymousUsage] = useState(() => {
    const saved = localStorage.getItem('anonymousUsage');
    return saved ? JSON.parse(saved) : { chat: 0, image: 0, video: 0 };
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true);
      fetchUserData();
    }
    
    applyTheme(theme);
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const applyTheme = (currentTheme) => {
    localStorage.setItem('theme', currentTheme);
    document.documentElement.className = currentTheme;
    document.documentElement.style.colorScheme = currentTheme;
    
    let metaThemeColor = document.querySelector('meta[name=theme-color]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.name = 'theme-color';
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.content = currentTheme === 'dark' ? '#1c1917' : '#fef3c7';
  };

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API_URL}/user/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      if (error.response?.status === 401) {
        logout();
      }
    }
  };

 const login = async (email, password) => {
  try {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user)); // ← ADD THIS LINE
    axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    setIsAuthenticated(true);
    setUser(response.data.user);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || 'Authentication failed'
    };
  }
};

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setUser(null);
    window.location.href = '/';
  };

  const toggleTheme = () => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'dark' ? 'light' : 'dark';
      return newTheme;
    });
  };

  const themeStyles = themes[theme] || themes.light;

  return (
    <HelmetProvider>
      <ThemeContext.Provider value={{ theme, toggleTheme, themeStyles }}>
        <Router>
          <div className={`min-h-screen ${themeStyles.background} ${themeStyles.text}`}>
            <Navbar
              isAuthenticated={isAuthenticated}
              user={user}
              logout={logout}
            />
            
            <ThemeTransition theme={theme} animationType="left-right">
              <Routes>
                <Route path="/" element={
                  isAuthenticated && user?.plan === 'paid' ?
                    <Navigate to="/chat" /> :
                    <HomePage isAuthenticated={isAuthenticated} user={user} />
                } />
                <Route path="/login" element={
                  isAuthenticated ?
                    <Navigate to={user?.plan === 'paid' ? "/chat" : "/"} /> :
                    <LoginPage login={login} />
                } />
                <Route path="/signup" element={
                  isAuthenticated ?
                    <Navigate to="/chat" /> :
                    <SignupPage login={login} />
                } />
                <Route path="/chat" element={
                  <ChatPage
                    isAuthenticated={isAuthenticated}
                    user={user}
                    anonymousUsage={anonymousUsage}
                    setAnonymousUsage={setAnonymousUsage}
                  />
                } />
                <Route path="/generate" element={
                  <GeneratePage
                    isAuthenticated={isAuthenticated}
                    user={user}
                    anonymousUsage={anonymousUsage}
                    setAnonymousUsage={setAnonymousUsage}
                  />
                } />
                <Route path="/history" element={
                  isAuthenticated ? <ChatHistoryPage /> : <Navigate to="/login" />
                } />
                <Route path="/profile" element={
                  isAuthenticated ? 
                    <ProfilePage user={user} logout={logout} /> : 
                    <Navigate to="/login" />
                } />
                <Route path="/images" element={
                  isAuthenticated ? 
                    <GeneratedImagesPage /> : 
                    <Navigate to="/login" />
                } />
                <Route path="/videos" element={
                  isAuthenticated ? 
                    <GeneratedVideosPage /> : 
                    <Navigate to="/login" />
                } />
                <Route path="/shared/chat/:shareId" element={<SharedChatView />} />
                <Route path="/pricing" element={<PricingPage user={user} isAuthenticated={isAuthenticated} />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/verify-email" element={<EmailVerificationPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/success" element={<PaymentSuccessPage />} />
                <Route path="/payment-callback" element={<PaymentCallbackPage />} />
                
                {/* ========== BLOG ROUTES ========== */}
                {/* Public blog routes - everyone can see these */}
                <Route path="/blog" element={<BlogListPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                
                {/* Admin blog route - only admin users can access */}
                <Route path="/admin/blog" element={<AdminBlog />} />
              </Routes>
            </ThemeTransition>

            {/* Footer */}
            <footer className={`mt-8 py-6 border-t ${themeStyles.border} ${themeStyles.background}`}>
              <div className="container mx-auto px-4">
                <div className="flex flex-col md:flex-row justify-between items-center">
                  <div className="mb-4 md:mb-0">
                    <div className="flex items-center space-x-3">
                      <img 
                        src="https://app.gospelflixstream.com/bibleai.png" 
                        alt="Bible AI"
                        className="w-6 h-6 rounded-lg object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/24/amber-600/white?text=B';
                        }}
                      />
                      <span className={`text-sm ${themeStyles.secondaryText}`}>
                        © {new Date().getFullYear()} Bible AI
                      </span>
                      <DoveIcon className={`w-3 h-3 ${themeStyles.accent}`} />
                    </div>
                  </div>
                  
                  <div className="flex space-x-6 mb-4 md:mb-0">
                    <Link to="/blog" className={`text-sm ${themeStyles.secondaryText} hover:${themeStyles.text} transition-colors`}>
                      Blog
                    </Link>
                    <Link to="/privacy" className={`text-sm ${themeStyles.secondaryText} hover:${themeStyles.text} transition-colors`}>
                      Privacy & Terms
                    </Link>
                    <Link to="/contact" className={`text-sm ${themeStyles.secondaryText} hover:${themeStyles.text} transition-colors`}>
                      Contact
                    </Link>
                    <a 
                      href="mailto:info@bibleai.top" 
                      className={`text-sm ${themeStyles.secondaryText} hover:${themeStyles.text} transition-colors`}
                    >
                      Support
                    </a>
                  </div>
                  
                  <ThemeToggle 
                    theme={theme} 
                    toggleTheme={toggleTheme} 
                    themeStyles={themeStyles} 
                  />
                </div>
              </div>
            </footer>
          </div>
        </Router>
      </ThemeContext.Provider>
    </HelmetProvider>
  );
}

export default App;